// src/dto/aap.dto.ts
import { z as z2 } from "zod";

// src/dto/primitives.ts
import { z } from "zod";

// src/lib/geo-codes.ts
var COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "MX", name: "Mexico" },
  { code: "GB", name: "United Kingdom" },
  { code: "AU", name: "Australia" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "NL", name: "Netherlands" }
];
var COUNTRY_INDEX = new Map(COUNTRIES.map((c) => [c.code, c]));
function countryLabel(code) {
  if (!code) return "";
  return COUNTRY_INDEX.get(code.toUpperCase())?.name ?? code.toUpperCase();
}
var US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
  { code: "AS", name: "American Samoa" },
  { code: "GU", name: "Guam" },
  { code: "MP", name: "Northern Mariana Islands" },
  { code: "PR", name: "Puerto Rico" },
  { code: "VI", name: "U.S. Virgin Islands" }
];
var US_STATE_INDEX = new Map(US_STATES.map((s) => [s.code, s]));
function usStateLabel(code) {
  if (!code) return "";
  return US_STATE_INDEX.get(code.toUpperCase())?.name ?? code.toUpperCase();
}
var CA_PROVINCES = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" }
];
var CA_PROVINCE_INDEX = new Map(CA_PROVINCES.map((p) => [p.code, p]));
function caProvinceLabel(code) {
  if (!code) return "";
  return CA_PROVINCE_INDEX.get(code.toUpperCase())?.name ?? code.toUpperCase();
}
function stateOptionsForCountry(country) {
  return country?.toUpperCase() === "CA" ? CA_PROVINCES : US_STATES;
}
var US_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu"
];
var CA_TIMEZONES = [
  "America/St_Johns",
  "America/Halifax",
  "America/Toronto",
  "America/Winnipeg",
  "America/Regina",
  "America/Edmonton",
  "America/Vancouver",
  "America/Whitehorse",
  "America/Iqaluit"
];
function timezonesForCountry(country) {
  return country?.toUpperCase() === "CA" ? CA_TIMEZONES : US_TIMEZONES;
}
function currencyForCountry(country) {
  return country?.toUpperCase() === "CA" ? "CAD" : "USD";
}

// src/dto/primitives.ts
var E164_RE = /^\+[1-9]\d{6,14}$/;
var US_ZIP_RE = /^\d{5}(-\d{4})?$/;
var CA_POSTAL_RE = /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/;
var EIN_RE = /^\d{2}-?\d{7}$/;
var VIN_RE = /^[A-HJ-NPR-Z0-9]{17}$/;
var PHONE_MESSAGE = "Use E.164 format, e.g. +15551234567.";
var ZIP_MESSAGE = "Use ZIP (12345) or ZIP+4 (12345-6789).";
var CA_POSTAL_MESSAGE = "Use a Canadian postal code, e.g. A1A 1A1.";
var ZIP_OR_POSTAL_MESSAGE = "Use a US ZIP (12345) or Canadian postal code (A1A 1A1).";
var STATE_MESSAGE = "Pick a US state (2-letter code).";
var STATE_OR_PROVINCE_MESSAGE = "Pick a US state or Canadian province (2-letter code).";
var COUNTRY_MESSAGE = "Pick a supported country (2-letter code).";
var EIN_MESSAGE = "Use a 9-digit EIN, e.g. 12-3456789.";
var VIN_MESSAGE = "VIN must be 17 characters \u2014 letters & digits, no I/O/Q.";
var US_STATE_CODES = new Set(US_STATES.map((s) => s.code));
var STATE_OR_PROVINCE_CODES = /* @__PURE__ */ new Set([
  ...US_STATES.map((s) => s.code),
  ...CA_PROVINCES.map((p) => p.code)
]);
var COUNTRY_CODES = new Set(COUNTRIES.map((c) => c.code));
var PhoneE164Schema = z.string().trim().regex(E164_RE, PHONE_MESSAGE);
var OptionalPhoneE164Schema = PhoneE164Schema.nullable().optional();
var UsZipSchema = z.string().trim().regex(US_ZIP_RE, ZIP_MESSAGE);
var OptionalUsZipSchema = UsZipSchema.nullable().optional();
var CaPostalCodeSchema = z.string().trim().regex(CA_POSTAL_RE, CA_POSTAL_MESSAGE);
var OptionalCaPostalCodeSchema = CaPostalCodeSchema.nullable().optional();
var ZipOrPostalCodeSchema = z.string().trim().refine((v) => US_ZIP_RE.test(v) || CA_POSTAL_RE.test(v), ZIP_OR_POSTAL_MESSAGE);
var OptionalZipOrPostalCodeSchema = ZipOrPostalCodeSchema.nullable().optional();
var UsStateCodeSchema = z.string().trim().transform((s) => s.toUpperCase()).pipe(z.string().refine((c) => US_STATE_CODES.has(c), STATE_MESSAGE));
var OptionalUsStateCodeSchema = UsStateCodeSchema.nullable().optional();
var StateOrProvinceCodeSchema = z.string().trim().transform((s) => s.toUpperCase()).pipe(z.string().refine((c) => STATE_OR_PROVINCE_CODES.has(c), STATE_OR_PROVINCE_MESSAGE));
var OptionalStateOrProvinceCodeSchema = StateOrProvinceCodeSchema.nullable().optional();
var CountryCodeSchema = z.string().trim().transform((s) => s.toUpperCase()).pipe(z.string().refine((c) => COUNTRY_CODES.has(c), COUNTRY_MESSAGE));
var OptionalCountryCodeSchema = CountryCodeSchema.nullable().optional();
var EinSchema = z.string().trim().regex(EIN_RE, EIN_MESSAGE);
var OptionalEinSchema = EinSchema.nullable().optional();
var VinSchema = z.string().trim().transform((s) => s.toUpperCase()).pipe(z.string().regex(VIN_RE, VIN_MESSAGE));
var OptionalVinSchema = VinSchema.nullable().optional();
var EmailSchema = z.string().trim().email("Enter a valid email address.");
var OptionalEmailSchema = EmailSchema.nullable().optional();
var UrlSchema = z.string().trim().url("Use a full URL, e.g. https://example.com.");
var OptionalUrlSchema = UrlSchema.nullable().optional();

// src/dto/aap.dto.ts
var AAP_EXTENSION_URI = "https://autoagentprotocol.org/extensions/a2a-automotive-retail/v0.1";
var AAP_SKILL_IDS = [
  "dealer.information",
  "inventory.facets",
  "inventory.search",
  "inventory.vehicle",
  "lead.submit"
];
var AAP_METHODS = [
  "dealer.information.request",
  "inventory.facets.request",
  "inventory.search.request",
  "inventory.vehicle.request",
  "lead.submit.request"
];
var AgentCardSkillSchema = z2.object({
  id: z2.string(),
  name: z2.string(),
  description: z2.string().optional()
});
var AgentCardExtensionSchema = z2.object({
  uri: z2.string(),
  params: z2.record(z2.unknown()).optional()
});
var AgentCardInterfaceSchema = z2.object({
  protocol_binding: z2.enum(["JSONRPC", "HTTP+JSON"])
});
var AgentCardSchema = z2.object({
  name: z2.string(),
  description: z2.string().optional(),
  url: z2.string().url(),
  version: z2.string(),
  documentationUrl: z2.string().url().optional(),
  provider: z2.object({
    organization: z2.string().optional(),
    url: z2.string().url().optional()
  }).optional(),
  defaultInputModes: z2.array(z2.string()).optional(),
  defaultOutputModes: z2.array(z2.string()).optional(),
  capabilities: z2.object({
    /**
     * Standard A2A capability flags. Boolean false is the
     * accurate value for v0.1 — we don't implement SSE
     * streaming, push notifications, or state-transition
     * history yet. A compliant client treats absent fields as
     * `false`, but emitting them explicitly is friendlier.
     */
    streaming: z2.boolean().optional(),
    pushNotifications: z2.boolean().optional(),
    stateTransitionHistory: z2.boolean().optional(),
    extensions: z2.array(AgentCardExtensionSchema).optional()
  }),
  /** AAP-required interface advertisement. */
  supported_interfaces: z2.array(AgentCardInterfaceSchema).optional(),
  skills: z2.array(AgentCardSkillSchema),
  authentication: z2.object({
    schemes: z2.array(z2.string()).optional()
  }).optional()
});
var JsonRpcRequestSchema = z2.object({
  jsonrpc: z2.literal("2.0"),
  id: z2.union([z2.string(), z2.number()]).optional(),
  method: z2.string(),
  params: z2.unknown().optional()
});
var JsonRpcErrorSchema = z2.object({
  code: z2.number().int(),
  message: z2.string(),
  data: z2.unknown().optional()
});
var DealerInformationRequestSchema = z2.object({}).optional();
var DealerInformationResponseSchema = z2.object({
  name: z2.string(),
  phone: z2.string().nullable(),
  email: z2.string().nullable(),
  website: z2.string().nullable(),
  address: z2.string().nullable(),
  timezone: z2.string().nullable(),
  salesHours: z2.record(z2.unknown()).nullable(),
  serviceHours: z2.record(z2.unknown()).nullable()
});
var InventoryQuerySchema = z2.object({
  search: z2.string().optional(),
  make: z2.array(z2.string()).optional(),
  model: z2.array(z2.string()).optional(),
  body: z2.array(z2.string()).optional(),
  fuel: z2.array(z2.string()).optional(),
  driveTrain: z2.array(z2.string()).optional(),
  year: z2.object({ from: z2.number().optional(), to: z2.number().optional() }).optional(),
  price: z2.object({ from: z2.number().optional(), to: z2.number().optional() }).optional(),
  mileage: z2.object({ from: z2.number().optional(), to: z2.number().optional() }).optional(),
  pagination: z2.object({
    skip: z2.number().int().min(0).default(0),
    limit: z2.number().int().min(1).max(100).default(20)
  }).optional()
});
var InventoryVehicleRequestSchema = z2.object({
  vin: z2.string()
});
var LeadSubmitRequestSchema = z2.object({
  customer: z2.object({
    firstName: z2.string().max(80).optional(),
    lastName: z2.string().max(80).optional(),
    email: z2.string().email().optional(),
    phone: PhoneE164Schema.optional(),
    addressLine1: z2.string().max(120).optional(),
    city: z2.string().max(80).optional(),
    state: StateOrProvinceCodeSchema.optional(),
    zip: ZipOrPostalCodeSchema.optional()
  }),
  intent: z2.enum(["sales", "service", "test_drive", "trade_in", "general"]).default("sales"),
  message: z2.string().optional(),
  vehicle: z2.object({
    vin: z2.string().optional(),
    year: z2.number().int().optional(),
    make: z2.string().optional(),
    model: z2.string().optional(),
    trim: z2.string().optional()
  }).optional()
});
var LeadSubmitResponseSchema = z2.object({
  leadId: z2.string(),
  contactId: z2.string()
});
var ConnectAapChannelSchema = z2.object({
  rooftopId: z2.string().uuid(),
  name: z2.string().trim().min(1).max(80),
  /**
   * The bare hostname the dealer hosts the agent card on, e.g.
   * `dealer.example.com`. Stored on `channels.config.aapDomain`
   * and used by the Validate flow to fetch
   * `https://<domain>/.well-known/agent-card.json`.
   */
  domain: z2.string().trim().toLowerCase().min(3).max(253).regex(/^(?!https?:\/\/)[a-z0-9.\-:]+$/, "Use a bare hostname like dealer.example.com.")
});
var AapValidationResultSchema = z2.object({
  ok: z2.boolean(),
  fetchedUrl: z2.string(),
  /** Reason the fetch / compare failed; null on success. */
  error: z2.string().nullable(),
  /**
   * When `ok=false` because the hosted JSON differs from the
   * expected one, this is the first diffing field path (e.g.
   * `skills[1].id`). Null when ok or when the issue isn't a
   * field diff (HTTP error, parse error).
   */
  mismatchPath: z2.string().nullable()
});

// src/dto/automation.dto.ts
import { z as z3 } from "zod";
var AutomationConditionsSchema = z3.object({
  /** Matches `contact.leadSource` (e.g. "lumika - SMS", "lumika - Website Chat"). */
  source: z3.string().trim().min(1).max(200).optional(),
  /**
   * Matches `lead.primaryChannel` — the canonical channel-type vocab
   * (sms, email, web, voice, messenger, instagram, whatsapp, telegram, api).
   */
  channel: z3.string().trim().min(1).max(50).optional()
}).strict();
var AssignActionConfigSchema = z3.object({ assignedUserId: z3.string().uuid() }).strict();
var AddTagActionConfigSchema = z3.object({ tagId: z3.string().uuid() }).strict();
var CreateTaskActionConfigSchema = z3.object({
  title: z3.string().trim().min(1).max(200),
  assignedUserId: z3.string().uuid().optional(),
  /** Hours from lead creation to set as dueAt; null/absent = no due date. */
  dueInHours: z3.number().int().min(1).max(8760).optional()
}).strict();
var AutomationActionTypeSchema = z3.enum(["assign", "add_tag", "create_task"]);
var AutomationRuleSchema = z3.object({
  id: z3.string().uuid(),
  dealershipId: z3.string().uuid(),
  name: z3.string(),
  enabled: z3.boolean(),
  trigger: z3.string(),
  conditions: AutomationConditionsSchema,
  actionType: AutomationActionTypeSchema,
  actionConfig: z3.union([
    AssignActionConfigSchema,
    AddTagActionConfigSchema,
    CreateTaskActionConfigSchema
  ]),
  createdByUserId: z3.string().uuid().nullable(),
  createdAt: z3.string().datetime(),
  updatedAt: z3.string().datetime()
});
var CreateAutomationRuleSchema = z3.object({
  name: z3.string().trim().min(1).max(200),
  enabled: z3.boolean().default(true),
  conditions: AutomationConditionsSchema.default({}),
  actionType: AutomationActionTypeSchema,
  actionConfig: z3.union([
    AssignActionConfigSchema,
    AddTagActionConfigSchema,
    CreateTaskActionConfigSchema
  ])
});
var UpdateAutomationRuleSchema = z3.object({
  name: z3.string().trim().min(1).max(200),
  enabled: z3.boolean(),
  conditions: AutomationConditionsSchema,
  actionType: AutomationActionTypeSchema,
  actionConfig: z3.union([
    AssignActionConfigSchema,
    AddTagActionConfigSchema,
    CreateTaskActionConfigSchema
  ])
}).partial();

// src/dto/admin-activity.dto.ts
import { z as z4 } from "zod";
var AdminActivitySchema = z4.object({
  id: z4.string().uuid(),
  adminId: z4.string().uuid(),
  /** Display name of the operator who performed the action. */
  adminName: z4.string(),
  dealershipId: z4.string().uuid().nullable(),
  rooftopId: z4.string().uuid().nullable(),
  entityType: z4.string().nullable(),
  entityAction: z4.string().nullable(),
  entityId: z4.string().uuid().nullable(),
  type: z4.string(),
  data: z4.record(z4.unknown()),
  createdAt: z4.string().datetime()
});
var AdminActivityListSchema = z4.array(AdminActivitySchema);

// src/dto/billing.dto.ts
import { z as z7 } from "zod";

// src/dto/bdc.dto.ts
import { z as z6 } from "zod";

// src/dto/integration.dto.ts
import { z as z5 } from "zod";
var IntegrationProviderSchema = z5.enum(["spaceauto", "adf"]);
var IntegrationProviderLabels = {
  spaceauto: "Space Auto",
  adf: "ADF Lead"
};
var IntegrationStatusSchema = z5.enum([
  "synced",
  "syncing",
  "backfilling",
  "errored"
]);
var IntegrationBackfillStateSchema = z5.object({
  phase: z5.enum(["contacts", "activities", "appointments", "tradeins"]),
  offset: z5.number().int().min(0),
  // Total is null until the first searchCustomers response tells us
  // how many rows exist; the UI shows "loading…" in the meantime.
  total: z5.number().int().min(0).nullable(),
  startedAt: z5.string().datetime()
});
var IntegrationLocationsMappingSchema = z5.record(z5.string().uuid(), z5.string().trim().min(1).max(255)).nullable();
var IntegrationSchema = z5.object({
  id: z5.string().uuid(),
  dealershipId: z5.string().uuid(),
  /**
   * Location scope. Null = the whole dealership. For ADF this gates
   * which events emit a lead (only the scoped rooftop's); for Space
   * Auto it picks where newly imported contacts land.
   */
  rooftopId: z5.string().uuid().nullable(),
  provider: IntegrationProviderSchema,
  tokenMasked: z5.string(),
  notes: z5.string().nullable(),
  includeUnauthorized: z5.boolean(),
  /** On/off switch — `false` pauses both pull and push without losing
   *  the linker rows. `status` reflects the last sync independently. */
  enabled: z5.boolean(),
  status: IntegrationStatusSchema,
  lastPulledAt: z5.string().datetime().nullable(),
  lastPulledError: z5.string().nullable(),
  backfillState: IntegrationBackfillStateSchema.nullable(),
  locationsMapping: IntegrationLocationsMappingSchema,
  /**
   * Optional historical-sync start point — when set, the pull cron
   * runs a one-time backfill of every SA customer whose
   * `createdAt > startSyncAt`, ordered by `createdAt asc`. UI surfaces
   * this so the operator can see what they configured.
   */
  startSyncAt: z5.string().datetime().nullable(),
  /** Set by the backend when the historical backfill finishes. */
  historicalSyncCompletedAt: z5.string().datetime().nullable(),
  createdAt: z5.string().datetime(),
  updatedAt: z5.string().datetime()
});
var IntegrationListSchema = z5.array(IntegrationSchema);
var CreateIntegrationSchema = z5.object({
  provider: IntegrationProviderSchema,
  token: z5.string().trim().min(1).max(512),
  /** Location scope — a rooftop id, or null/absent for the whole dealership. */
  rooftopId: z5.string().uuid().nullable().optional(),
  notes: z5.string().trim().max(2e3).optional(),
  includeUnauthorized: z5.boolean().default(false),
  locationsMapping: IntegrationLocationsMappingSchema.optional(),
  /**
   * ISO 8601 datetime. When set, the pull cron does a one-time
   * historical backfill of every SA customer whose
   * `createdAt > startSyncAt`. Default null = no backfill.
   */
  startSyncAt: z5.string().datetime().nullable().optional()
}).superRefine((data, ctx) => {
  if (data.provider === "adf" && !z5.string().email().safeParse(data.token).success) {
    ctx.addIssue({
      path: ["token"],
      code: "custom",
      message: "Enter a valid recipient email."
    });
  }
});
var UpdateIntegrationSchema = z5.object({
  token: z5.string().trim().min(1).max(512).optional(),
  /** Location scope — a rooftop id, or null to widen back to the whole dealership. */
  rooftopId: z5.string().uuid().nullable().optional(),
  notes: z5.string().trim().max(2e3).optional(),
  includeUnauthorized: z5.boolean().optional(),
  enabled: z5.boolean().optional(),
  locationsMapping: IntegrationLocationsMappingSchema.optional(),
  /**
   * Re-setting `startSyncAt` resets the historical-sync watermark
   * so the cron re-runs the backfill from the new start point.
   * Setting it to `null` clears the watermark and disables backfill.
   */
  startSyncAt: z5.string().datetime().nullable().optional()
});
var VerifyIntegrationTokenRequestSchema = z5.object({
  provider: IntegrationProviderSchema,
  token: z5.string().trim().min(1).max(512)
});
var VerifyIntegrationLocationSchema = z5.object({
  id: z5.string(),
  name: z5.string(),
  timezone: z5.string().nullable(),
  city: z5.string().nullable(),
  state: z5.string().nullable()
});
var VerifyIntegrationTokenResponseSchema = z5.object({
  ok: z5.literal(true),
  dealership: z5.object({
    id: z5.string(),
    name: z5.string().nullable(),
    locations: z5.array(VerifyIntegrationLocationSchema)
  })
});

// src/dto/bdc.dto.ts
var OptionalTextSchema = z6.string().trim().min(1).nullable().optional();
var OptionalEmailSchema2 = z6.string().email().nullable().optional();
var OptionalUrlSchema2 = z6.string().url().nullable().optional();
var DateSchema = z6.string().regex(/^\d{4}-\d{2}-\d{2}$/);
var JsonObjectSchema = z6.record(z6.unknown());
var JsonArraySchema = z6.array(z6.unknown());
var PageNumberSchema = z6.number().int().positive();
var CONTACT_LANGUAGES = [
  { code: "en", label: "English", voiceLocale: "en-US" },
  { code: "es", label: "Spanish", voiceLocale: "es-US" },
  { code: "ru", label: "Russian", voiceLocale: "ru-RU" }
];
var ContactLanguageSchema = z6.enum(["en", "es", "ru"]);
function normalizeContactLanguage(value) {
  if (!value) return null;
  const v = value.trim().toLowerCase();
  for (const lang of CONTACT_LANGUAGES) {
    if (v === lang.code || v === lang.label.toLowerCase() || v === lang.voiceLocale.toLowerCase()) {
      return lang.code;
    }
  }
  return null;
}
function contactLanguageLabel(code) {
  if (code) {
    const found = CONTACT_LANGUAGES.find((l) => l.code === code);
    if (found) return found.label;
  }
  return "English";
}
function contactLanguageVoiceLocale(code) {
  if (!code || code === "en") return null;
  const found = CONTACT_LANGUAGES.find((l) => l.code === code);
  return found ? found.voiceLocale : null;
}
var PreferredCommunicationChannelSchema = z6.enum([
  "voice",
  "sms",
  "email",
  "web",
  "messenger",
  "instagram",
  "whatsapp",
  "telegram"
]);
var ContactChannelTypeSchema = z6.enum([
  "phone",
  "email",
  "web",
  "telegram",
  "messenger",
  "instagram",
  "whatsapp"
]);
var ContactStatusSchema = z6.enum([
  "anonymous",
  "new_lead",
  "engaged",
  "appointment_scheduled",
  "appointment_attended",
  "sold",
  "service_customer",
  "inactive",
  "lost"
]);
var VehicleStatusSchema = z6.enum([
  "available",
  "intransit",
  "pending",
  "sold"
]);
var VehicleConditionSchema = z6.enum(["new", "used", "certified"]);
var VehicleSourceSchema = z6.enum(["feed", "manual", "api"]);
var ChannelTypeSchema = z6.enum([
  "phone",
  // SMS is its own channel even though it shares the underlying
  // E.164 with the `phone` (voice) row — the 10DLC compliance gate
  // fires independently from voice readiness, and the dispatcher /
  // inbox / outbound sender treat them as separate inboxes. See
  // `telephony-numbers.service.ts ensureSmsChannel` for the
  // provisioning lifecycle and the `blocked_compliance` status.
  "sms",
  "email_alias",
  "gmail_oauth",
  "outlook_oauth",
  "email_adf_inbound",
  "web",
  "web_form",
  "facebook_messenger",
  "instagram",
  "whatsapp",
  "telegram",
  "api",
  "aap"
]);
var PROVIDER_MANAGED_CHANNEL_TYPES = [
  "facebook_messenger",
  "instagram",
  "whatsapp",
  "telegram"
];
function isProviderManagedChannelType(type) {
  return PROVIDER_MANAGED_CHANNEL_TYPES.includes(type);
}
var ChannelStatusSchema = z6.enum([
  "draft",
  "provisioning",
  "active",
  "paused",
  "failing",
  "released",
  // Provisioned but cannot send/receive yet because an external
  // compliance gate hasn't cleared. Today this is set on
  // `type='sms'` rows whose phone number is still awaiting 10DLC
  // campaign approval; voice on the same number remains `active`.
  // The UI surfaces an explicit disclaimer; the outbound
  // dispatcher refuses to send.
  "blocked_compliance"
]);
var ChannelCapabilitySchema = z6.enum(["sms", "mms", "voice"]);
var OPERATOR_PAUSABLE_CHANNEL_TYPES = [
  "email_alias",
  "gmail_oauth",
  "outlook_oauth",
  "web",
  "web_form",
  "api",
  "aap",
  "facebook_messenger",
  "instagram",
  "whatsapp",
  "telegram"
];
function isOperatorPausableChannelType(type) {
  return OPERATOR_PAUSABLE_CHANNEL_TYPES.includes(type);
}
function channelStatusActions(type, status) {
  if (!isOperatorPausableChannelType(type)) return [];
  switch (status) {
    case "active":
      return [{ action: "pause", label: "Pause", target: "paused" }];
    case "paused":
      return [{ action: "activate", label: "Activate", target: "active" }];
    case "failing":
      return [
        { action: "activate", label: "Activate", target: "active" },
        { action: "pause", label: "Pause", target: "paused" }
      ];
    default:
      return [];
  }
}
var LeadPrimaryChannelSchema = z6.enum([
  "sms",
  "voice",
  "email",
  "web",
  "web_form",
  "messenger",
  "instagram",
  "whatsapp",
  "telegram",
  "aap"
]);
var ActivityDirectionSchema = z6.enum(["inbound", "outbound", "internal", "system"]);
var ActivityActorTypeSchema = z6.enum(["contact", "user", "ai", "system"]);
var DeliveryStatusSchema = z6.enum([
  "queued",
  // Transient claim state: the outbox consumer compare-and-swaps
  // `queued` → `sending` before calling the provider so SQS
  // at-least-once redeliveries can't double-send. Released back to
  // `queued` on retryable errors; otherwise ends at `sent`/`failed`.
  "sending",
  "sent",
  "delivered",
  "failed",
  "received",
  "read"
]);
var AppointmentTypeSchema = z6.enum([
  "test_drive",
  "trade_appraisal",
  "delivery",
  "sales",
  "service",
  "parts",
  "phone",
  "virtual",
  "other"
]);
var AppointmentStatusSchema = z6.enum([
  "unconfirmed",
  "pending_customer",
  "confirmed",
  "checked_in",
  "in_service",
  "ready_for_pickup",
  "completed",
  "cancelled",
  "no_show",
  "rescheduled"
]);
var AppointmentTransportTypeSchema = z6.enum(["in_person", "phone", "video"]);
var LeadStatusSchema = z6.enum(["new", "working", "appointment", "won", "lost"]);
var ContactSchema = z6.object({
  id: z6.string().uuid(),
  dealershipId: z6.string().uuid(),
  status: ContactStatusSchema,
  firstName: z6.string().nullable(),
  lastName: z6.string().nullable(),
  avatarUrl: z6.string().nullable(),
  businessAccount: z6.boolean(),
  businessName: z6.string().nullable(),
  primaryEmail: z6.string().email().nullable(),
  primaryPhone: z6.string().nullable(),
  // Last-resort display label for contacts with no name/email/phone — a
  // short snippet of their first inbound message — so the UI shows
  // something identifying instead of "Anonymous". Computed at read time on
  // list/detail endpoints; null when the contact has no inbound message.
  fallbackLabel: z6.string().nullable(),
  // Server-computed canonical display label (businessName → first+last →
  // email → phone → socialHandle → fallbackLabel → "Anonymous"). Always
  // populated by backend mappers; nullable/optional here for frontend
  // optimistic constructions that may not have the full contact available.
  displayName: z6.string().nullable().optional(),
  state: z6.string().nullable(),
  city: z6.string().nullable(),
  zip: z6.string().nullable(),
  addressLine1: z6.string().nullable(),
  timezone: z6.string().nullable(),
  preferredLanguage: ContactLanguageSchema.nullable(),
  preferredChannel: PreferredCommunicationChannelSchema.nullable(),
  birthDate: DateSchema.nullable(),
  assignedUserId: z6.string().uuid().nullable(),
  assignedAt: z6.string().datetime().nullable(),
  lastActivityAt: z6.string().datetime().nullable(),
  leadSource: z6.string().nullable(),
  utmSource: z6.string().nullable(),
  utmMedium: z6.string().nullable(),
  utmCampaign: z6.string().nullable(),
  optoutEmail: z6.boolean(),
  optoutSms: z6.boolean(),
  optoutVoice: z6.boolean(),
  aiAutopilot: z6.boolean(),
  notes: z6.string().nullable(),
  internalNotes: z6.string().nullable(),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime(),
  // Timestamp of the most recent inbound or outbound message (sms/email/social).
  lastMessageAt: z6.string().datetime().nullable(),
  // Per-channel message counts (read-time). Enriched by the list endpoint.
  channelCounts: z6.object({
    phone: z6.number().int().nonnegative(),
    sms: z6.number().int().nonnegative(),
    email: z6.number().int().nonnegative(),
    social: z6.number().int().nonnegative()
  }),
  // Total activity count across all leads for this contact. Enriched by the list endpoint.
  activityCount: z6.number().int().nonnegative(),
  // Vehicles the contact is shopping for (joined to inventory where linked).
  vehiclesOfInterest: z6.array(
    z6.object({
      vin: z6.string().nullable(),
      year: z6.number().int().nullable(),
      make: z6.string().nullable(),
      model: z6.string().nullable(),
      trim: z6.string().nullable(),
      status: VehicleStatusSchema.nullable()
    })
  ),
  // Vehicles the contact owns (free-text from contact_vehicles).
  vehiclesOwning: z6.array(
    z6.object({
      year: z6.number().int().nullable(),
      make: z6.string().nullable(),
      model: z6.string().nullable(),
      trim: z6.string().nullable()
    })
  ),
  // All lead pipeline statuses this contact currently holds.
  leadStatuses: z6.array(LeadStatusSchema),
  // Most-recent lead id per channel bucket — populated by the contacts-list
  // enrichment pass (one batched DISTINCT ON query, no per-row fetch).
  // Null when the contact has no lead on that channel. Used by the Contacts
  // table quick-channel buttons to deep-link /inbox/:leadId.
  recentLeadByChannel: z6.object({
    phone: z6.string().uuid().nullable(),
    sms: z6.string().uuid().nullable(),
    email: z6.string().uuid().nullable(),
    social: z6.string().uuid().nullable()
  })
});
var CreateContactSchema = z6.object({
  dealershipId: z6.string().uuid(),
  status: ContactStatusSchema.default("anonymous"),
  firstName: z6.string().trim().min(1).max(120).nullable().optional(),
  lastName: z6.string().trim().min(1).max(120).nullable().optional(),
  businessAccount: z6.boolean().default(false),
  businessName: OptionalTextSchema,
  primaryEmail: OptionalEmailSchema2,
  primaryPhone: OptionalPhoneE164Schema,
  state: OptionalStateOrProvinceCodeSchema,
  city: z6.string().trim().min(1).max(120).nullable().optional(),
  zip: OptionalZipOrPostalCodeSchema,
  addressLine1: z6.string().trim().min(1).max(200).nullable().optional(),
  timezone: OptionalTextSchema,
  preferredLanguage: ContactLanguageSchema.nullable().optional(),
  preferredChannel: PreferredCommunicationChannelSchema.nullable().optional(),
  birthDate: DateSchema.nullable().optional(),
  assignedUserId: z6.string().uuid().nullable().optional(),
  assignedAt: z6.string().datetime().nullable().optional(),
  leadSource: OptionalTextSchema,
  utmSource: OptionalTextSchema,
  utmMedium: OptionalTextSchema,
  utmCampaign: OptionalTextSchema,
  optoutEmail: z6.boolean().default(false),
  optoutSms: z6.boolean().default(false),
  optoutVoice: z6.boolean().default(false),
  aiAutopilot: z6.boolean().optional(),
  notes: z6.string().nullable().optional(),
  internalNotes: z6.string().nullable().optional()
});
var UpdateContactSchema = CreateContactSchema.omit({ dealershipId: true }).partial();
var ContactChannelSchema = z6.object({
  id: z6.string().uuid(),
  dealershipId: z6.string().uuid(),
  contactId: z6.string().uuid(),
  type: ContactChannelTypeSchema,
  value: z6.string().nullable(),
  externalId: z6.string(),
  label: z6.string().nullable(),
  isPrimary: z6.boolean(),
  isMarketingOptedIn: z6.boolean(),
  transactionalOptedOutAt: z6.string().datetime().nullable(),
  verifiedAt: z6.string().datetime().nullable(),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
var ContactChannelInputObject = z6.object({
  dealershipId: z6.string().uuid(),
  contactId: z6.string().uuid(),
  type: ContactChannelTypeSchema,
  value: OptionalTextSchema,
  externalId: z6.string().trim().min(1),
  label: OptionalTextSchema,
  isPrimary: z6.boolean().default(false),
  isMarketingOptedIn: z6.boolean().default(false),
  transactionalOptedOutAt: z6.string().datetime().nullable().optional(),
  verifiedAt: z6.string().datetime().nullable().optional()
});
function refineContactChannelValue(data, ctx) {
  for (const field of ["value", "externalId"]) {
    const raw = data[field];
    if (!raw) continue;
    if (data.type === "email" && !z6.string().email().safeParse(raw).success) {
      ctx.addIssue({
        code: z6.ZodIssueCode.custom,
        path: [field],
        message: "Enter a valid email address."
      });
    }
    if ((data.type === "phone" || data.type === "whatsapp") && !PhoneE164Schema.safeParse(raw).success) {
      ctx.addIssue({
        code: z6.ZodIssueCode.custom,
        path: [field],
        message: "Use E.164 format, e.g. +15551234567."
      });
    }
  }
}
var CreateContactChannelSchema = ContactChannelInputObject.superRefine(refineContactChannelValue);
var CreateContactChannelBodySchema = ContactChannelInputObject.omit({
  dealershipId: true
}).superRefine(refineContactChannelValue);
var UpdateContactChannelSchema = ContactChannelInputObject.omit({
  dealershipId: true,
  contactId: true
}).partial().superRefine(refineContactChannelValue);
var InventoryVehicleSchema = z6.object({
  id: z6.string().uuid(),
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid(),
  // `null` for manually-created vehicles (no inventory feed brought
  // them in). Feed-managed vehicles carry the source import's id.
  importId: z6.string().uuid().nullable(),
  vin: z6.string(),
  stock: z6.string().nullable(),
  status: VehicleStatusSchema,
  condition: VehicleConditionSchema.nullable(),
  year: z6.number().int().nullable(),
  make: z6.string().nullable(),
  model: z6.string().nullable(),
  trim: z6.string().nullable(),
  body: z6.string().nullable(),
  color: z6.string().nullable(),
  interiorColor: z6.string().nullable(),
  mileage: z6.number().int().nullable(),
  driveTrain: z6.string().nullable(),
  transmission: z6.string().nullable(),
  engine: z6.string().nullable(),
  fuel: z6.string().nullable(),
  doors: z6.number().int().nullable(),
  seats: z6.number().int().nullable(),
  features: z6.array(z6.string()).nullable(),
  msrp: z6.number().nullable(),
  price: z6.number().nullable(),
  listPrice: z6.number().nullable(),
  offeredPrice: z6.number().nullable(),
  photos: z6.array(z6.string()).nullable(),
  vdpUrl: z6.string().url().nullable(),
  description: z6.string().nullable(),
  source: VehicleSourceSchema.nullable(),
  lastSyncedAt: z6.string().datetime().nullable(),
  hash: z6.string().nullable(),
  stockDate: z6.string().datetime(),
  soldDate: z6.string().datetime().nullable(),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
var CreateInventoryVehicleSchema = z6.object({
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid(),
  vin: z6.string().trim().min(1),
  stock: OptionalTextSchema,
  status: VehicleStatusSchema.default("available"),
  condition: VehicleConditionSchema.nullable().optional(),
  year: z6.number().int().nullable().optional(),
  make: OptionalTextSchema,
  model: OptionalTextSchema,
  trim: OptionalTextSchema,
  body: OptionalTextSchema,
  color: OptionalTextSchema,
  interiorColor: OptionalTextSchema,
  mileage: z6.number().int().nullable().optional(),
  driveTrain: OptionalTextSchema,
  transmission: OptionalTextSchema,
  engine: OptionalTextSchema,
  fuel: OptionalTextSchema,
  doors: z6.number().int().nonnegative().nullable().optional(),
  seats: z6.number().int().nonnegative().nullable().optional(),
  features: z6.array(z6.string().trim().min(1)).nullable().optional(),
  msrp: z6.number().nullable().optional(),
  price: z6.number().nullable().optional(),
  listPrice: z6.number().nullable().optional(),
  offeredPrice: z6.number().nullable().optional(),
  photos: z6.array(z6.string()).nullable().optional(),
  vdpUrl: OptionalUrlSchema2,
  description: OptionalTextSchema,
  source: VehicleSourceSchema.nullable().optional(),
  lastSyncedAt: z6.string().datetime().nullable().optional()
});
var UpdateInventoryVehicleSchema = CreateInventoryVehicleSchema.omit({
  dealershipId: true,
  vin: true
}).partial();
var ChannelSchema = z6.object({
  id: z6.string().uuid(),
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid().nullable(),
  type: ChannelTypeSchema,
  name: z6.string(),
  externalAccountId: z6.string().nullable(),
  providerResourceId: z6.string().nullable(),
  externalAccountLabel: z6.string().nullable(),
  /** CDN URL of the connected account's avatar, or null. */
  externalAccountAvatarUrl: z6.string().nullable(),
  /**
   * Computed (read-only) link that opens the connected account/inbox:
   * `m.me/<page>` for Messenger, `ig.me/m/<user>` for Instagram,
   * `wa.me/<e164>` for WhatsApp, `t.me/<bot>` for Telegram,
   * `mailto:<addr>` for email channels. Null when the channel type
   * has no meaningful external link (api/web/etc.) or the identifier
   * isn't known yet.
   */
  deepLink: z6.string().nullable(),
  e164: z6.string().nullable(),
  capabilities: z6.array(ChannelCapabilitySchema).nullable(),
  forwardingNumber: z6.string().nullable(),
  voicemailEnabled: z6.boolean().nullable(),
  /** Phone-only: multi-language voice mode (en/es/ru). Null elsewhere. */
  multilingual: z6.boolean().nullable(),
  /**
   * email_alias addressing. The inbox is
   * `<emailLocalPart>@<emailSubdomain>.<zone>`; both are null on every
   * other channel type.
   */
  emailLocalPart: z6.string().nullable(),
  emailSubdomain: z6.string().nullable(),
  auth: JsonObjectSchema,
  config: JsonObjectSchema,
  /**
   * Per-channel AI response delay, in seconds. How long the AI user
   * waits before auto-replying on this channel — the "give humans
   * first dibs" window. Null falls back to the agent-level default.
   */
  aiResponseDelaySeconds: z6.number().int().min(0).nullable(),
  /**
   * When true, new leads on this channel and existing open
   * leads that currently have no assignee are routed to the
   * dealership's AI user instead of through the human round-robin.
   * Server-side defaulted to true for `web` and `api` channels at
   * creation; otherwise false. Operators can toggle from the channel
   * settings UI at any time.
   */
  alwaysAssignToAi: z6.boolean(),
  status: ChannelStatusSchema,
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
var CreateChannelSchema = z6.object({
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid().nullable(),
  type: z6.enum(["web", "api"]),
  name: z6.string().trim().min(1),
  config: JsonObjectSchema.default({}),
  aiResponseDelaySeconds: z6.number().int().min(0).nullable().optional(),
  alwaysAssignToAi: z6.boolean().optional(),
  status: ChannelStatusSchema.default("active")
});
var UpdateChannelSchema = z6.object({
  rooftopId: z6.string().uuid().nullable(),
  type: ChannelTypeSchema,
  name: z6.string().trim().min(1),
  forwardingNumber: OptionalPhoneE164Schema,
  voicemailEnabled: z6.boolean().nullable(),
  multilingual: z6.boolean().nullable(),
  config: JsonObjectSchema,
  aiResponseDelaySeconds: z6.number().int().min(0).nullable(),
  alwaysAssignToAi: z6.boolean(),
  status: ChannelStatusSchema
}).partial();
var AllowedDomainsSchema = z6.array(
  z6.string().trim().toLowerCase().min(1).max(253).regex(/^[a-z0-9.\-:*]+$/, "Enter a bare hostname, e.g. example.com.")
).max(50).optional();
var ConnectApiChannelSchema = z6.object({
  rooftopId: z6.string().uuid().nullable(),
  name: z6.string().trim().min(1).max(80),
  allowedDomains: AllowedDomainsSchema
});
var ConnectWidgetChannelSchema = z6.object({
  rooftopId: z6.string().uuid().nullable(),
  name: z6.string().trim().min(1).max(80),
  allowedDomains: AllowedDomainsSchema
});
var EmailSubdomainSchema = z6.string().trim().toLowerCase().min(1).max(63).regex(
  /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/,
  "Use 1-63 lowercase letters, numbers, or hyphens."
);
var EmailLocalPartSchema = z6.string().trim().toLowerCase().min(1).max(64).regex(
  /^[a-z0-9](?:[a-z0-9._+-]{0,62}[a-z0-9])?$/,
  "Use 1-64 lowercase letters, numbers, dots, underscores, pluses, or hyphens."
);
var ConnectEmailChannelSchema = z6.object({
  rooftopId: z6.string().uuid().nullable(),
  name: z6.string().trim().min(1).max(80),
  localPart: EmailLocalPartSchema,
  subdomain: EmailSubdomainSchema
});
var CheckEmailAddressSchema = z6.object({
  localPart: EmailLocalPartSchema,
  subdomain: EmailSubdomainSchema
});
var EmailAddressAvailabilitySchema = z6.object({
  localPart: z6.string(),
  subdomain: z6.string(),
  /** The full address `<localPart>@<subdomain>.<zone>` (server-resolved zone). */
  address: z6.string(),
  available: z6.boolean(),
  /** The subdomain is owned by a DIFFERENT dealership. */
  subdomainTaken: z6.boolean(),
  /** This dealership already has this local-part on this subdomain. */
  localPartTaken: z6.boolean()
});
var HexColor = z6.string().regex(/^#[0-9a-fA-F]{6}$/u, "Use #rrggbb hex (e.g. #133b54)");
var BrandColorPairSchema = z6.object({
  background: HexColor,
  foreground: HexColor
});
var BrandImageSchema = z6.object({
  url: z6.string().url("Logo must be a fully qualified URL"),
  altText: z6.string().max(120).optional(),
  width: z6.string().max(8).optional(),
  height: z6.string().max(8).optional()
});
var WidgetBrandSchema = z6.object({
  colors: z6.object({
    primary: z6.array(BrandColorPairSchema).max(2).optional(),
    secondary: z6.array(BrandColorPairSchema).max(2).optional()
  }).optional(),
  logo: z6.object({
    image: BrandImageSchema.optional()
  }).optional()
});
var WidgetCustomizationSchema = z6.object({
  title: z6.string().trim().max(60).optional(),
  subtitle: z6.string().trim().max(120).optional(),
  chatWelcomeHeading: z6.string().trim().max(120).optional(),
  chatWelcomeDescription: z6.string().trim().max(280).optional(),
  chatWelcomeQuickQuestions: z6.array(z6.string().trim().min(1).max(120)).max(4).optional(),
  brand: WidgetBrandSchema.optional()
});
var ConnectTelegramChannelSchema = z6.object({
  rooftopId: z6.string().uuid(),
  name: z6.string().trim().min(1).max(80),
  /** Token from @BotFather: `<bot_id>:<random>`. */
  botToken: z6.string().trim().regex(/^\d+:[A-Za-z0-9_-]{20,}$/, "Invalid Telegram bot token"),
  /**
   * Set true to proceed when the bot is already connected to THIS
   * dealership — the existing connection is replaced (transferred) in
   * place. The first attempt is made without it; a same-dealership
   * conflict returns a 409 carrying `error: "ChannelConflict"`, the UI
   * shows a prominent warning, and re-submits with this flag set.
   */
  confirmTransfer: z6.boolean().optional()
});
var ConnectWhatsAppEmbeddedSignupSchema = z6.object({
  rooftopId: z6.string().uuid(),
  name: z6.string().trim().min(1).max(80),
  code: z6.string().trim().min(1),
  phoneNumberId: z6.string().trim().min(1),
  wabaId: z6.string().trim().min(1),
  /** See ConnectTelegramChannelSchema.confirmTransfer — same contract. */
  confirmTransfer: z6.boolean().optional()
});
var ChannelConflictResponseSchema = z6.object({
  statusCode: z6.literal(409),
  error: z6.literal("ChannelConflict"),
  message: z6.string(),
  conflict: z6.object({
    channelType: ChannelTypeSchema,
    accountLabel: z6.string(),
    sameDealership: z6.boolean()
  })
});
var StartMetaOAuthSchema = z6.object({
  rooftopId: z6.string().uuid(),
  channelType: z6.enum(["facebook_messenger", "instagram"])
});
var ChannelOnboardingInfoSchema = z6.object({
  channel: ChannelSchema,
  /** For API + widget: the token to embed. */
  token: z6.string().nullable(),
  /** For API + widget: HTTPS base URL of the conversation endpoints. */
  apiBaseUrl: z6.string().nullable(),
  /** For widget: WSS URL of the realtime conversation channel (port: WS-based runtime). */
  websocketUrl: z6.string().nullable(),
  /** For widget: HTML snippet the dealer pastes into their site. */
  embedSnippet: z6.string().nullable(),
  /** For Telegram: public webhook URL registered via setWebhook. */
  telegramWebhookUrl: z6.string().nullable(),
  /** For Telegram: link to BotFather conversation. */
  botFatherUrl: z6.string().nullable(),
  /** For Meta: the public webhook URL to register in the Meta dashboard. */
  metaWebhookUrl: z6.string().nullable(),
  /** For Meta: subscribed_fields the dealer needs to enable. */
  metaSubscribedFields: z6.array(z6.string()).nullable(),
  /** Human-readable next step. */
  instructions: z6.string()
});
var ContactIntegrationSyncSchema = z6.object({
  id: z6.string().uuid(),
  integrationId: z6.string().uuid(),
  provider: z6.enum(["spaceauto", "adf"]),
  integrationStatus: IntegrationStatusSchema,
  externalId: z6.string(),
  // Last `updated_at` we observed on each side at the last reconcile.
  externalUpdatedAt: z6.string().datetime(),
  lumikaUpdatedAt: z6.string().datetime(),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
function computeLeadScore(lead) {
  if (lead.status === "won" || lead.status === "lost") return 0;
  let score = 0;
  const lastAt = lead.lastMessageAt ?? lead.lastActivityAt;
  if (lastAt) {
    const hoursAgo = (Date.now() - new Date(lastAt).getTime()) / 36e5;
    if (hoursAgo < 1) score += 30;
    else if (hoursAgo < 6) score += 22;
    else if (hoursAgo < 24) score += 14;
    else if (hoursAgo < 72) score += 6;
  }
  if (lead.status === "working") score += 20;
  else if (lead.status === "appointment") score += 15;
  else if (lead.status === "new") score += 10;
  if (lead.escalated || lead.needsHumanAt != null) score += 20;
  if (lead.unreadAt != null) score += 15;
  if (lead.slaDueAt != null) score += 10;
  if (lead.interestVehicleId != null || lead.interestVehicle != null) score += 5;
  return Math.min(score, 100);
}
var LeadSchema = z6.object({
  id: z6.string().uuid(),
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid().nullable(),
  contactId: z6.string().uuid().nullable(),
  // Denormalized from the contact so inbox rows can render the contact's
  // name/avatar without a separate fetch-all-contacts call. Null when the
  // contact wasn't joined (e.g. single conversation fetches / save paths).
  contactName: z6.string().nullable(),
  contactAvatarUrl: z6.string().nullable(),
  // Last-resort label when the contact has no name — a short snippet of the
  // conversation's first inbound message — so anonymous inbox rows read as
  // something identifying instead of "Anonymous". Computed at read time.
  fallbackLabel: z6.string().nullable(),
  // Server-computed canonical display label — the single resolved name for
  // the inbox row (businessName → first+last → email → phone →
  // socialHandle → fallbackLabel → "Anonymous"). Fixes the nameless-phone-
  // call bug where the snippet beat the phone number. Always populated by
  // the backend; nullable/optional here for frontend optimistic builds.
  displayName: z6.string().nullable().optional(),
  primaryChannel: LeadPrimaryChannelSchema,
  channelId: z6.string().uuid().nullable(),
  // Opportunity fields: department, pipeline stage, vehicle of interest, free text.
  type: z6.enum(["sales", "service", "parts"]).nullable(),
  status: z6.enum(["new", "working", "appointment", "won", "lost"]),
  lostReason: z6.string().nullable(),
  interestVehicleId: z6.string().uuid().nullable(),
  interest: z6.string().nullable(),
  closed: z6.boolean(),
  closedAt: z6.string().datetime().nullable(),
  snoozedUntil: z6.string().datetime().nullable(),
  aiAutopilot: z6.boolean(),
  aiSuppressedUntil: z6.string().datetime().nullable(),
  escalated: z6.boolean(),
  // Set when the AI flagged this conversation for a human to close
  // (AI Task Management — `flag_for_human`). The AI keeps assisting;
  // the UI shows a "Needs you" banner + inbox filter. Null = not flagged.
  needsHumanAt: z6.string().datetime().nullable(),
  needsHumanReason: z6.string().nullable(),
  assignedUserId: z6.string().uuid().nullable(),
  lastActivityAt: z6.string().datetime().nullable(),
  lastActivityPreview: z6.string().nullable(),
  // Timestamp of the most recent inbound or outbound message (sms/email/social).
  // Excludes calls, notes, and system events. Null on leads with no messaging activity.
  lastMessageAt: z6.string().datetime().nullable(),
  // Joined vehicle of interest (resolved from interestVehicleId). Includes
  // sold-but-attached vehicles. Null when no primary vehicle is linked.
  interestVehicle: z6.object({
    vin: z6.string(),
    year: z6.number().int().nullable(),
    make: z6.string().nullable(),
    model: z6.string().nullable(),
    trim: z6.string().nullable(),
    status: VehicleStatusSchema
  }).nullable(),
  // Per-channel message counts (read-time, no stored column).
  channelCounts: z6.object({
    phone: z6.number().int().nonnegative(),
    sms: z6.number().int().nonnegative(),
    email: z6.number().int().nonnegative(),
    social: z6.number().int().nonnegative()
  }),
  // Sender of the most recent conversational turn (message/call) in the
  // thread. Drives the inbox preview prefix ("You: / AI: / <rep>:") so the
  // queue shows by whom the last message was sent. Populated only on the
  // inbox feed (the keyset list derives it); null on single fetches.
  lastActorType: z6.enum(["contact", "user", "ai", "system"]).nullable(),
  lastActorUserId: z6.string().uuid().nullable(),
  // Cached AI "recent conversation history" narrative for the Customer 360
  // panel. Null until first generated; `aiSummaryGeneratedAt` is its
  // freshness mark (regenerated when it falls behind `lastActivityAt`).
  aiSummary: z6.string().nullable(),
  aiSummaryGeneratedAt: z6.string().datetime().nullable(),
  unreadAt: z6.string().datetime().nullable(),
  slaDueAt: z6.string().datetime().nullable(),
  // Derived at read time by computeLeadScore — NOT a stored column, NOT ML.
  // A deterministic 0–100 proxy for lead engagement based on recency, status,
  // escalation signals, unread, SLA, and vehicle-of-interest presence.
  leadScore: z6.number().int().min(0).max(100),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
var LeadAiSummaryResultSchema = z6.object({
  aiSummary: z6.string().nullable(),
  aiSummaryGeneratedAt: z6.string().datetime().nullable()
});
var MergeContactsSchema = z6.object({
  winnerContactId: z6.string().uuid(),
  loserContactIds: z6.array(z6.string().uuid()).min(1).max(3),
  fieldOverrides: z6.object({
    firstName: z6.string().nullable().optional(),
    lastName: z6.string().nullable().optional(),
    primaryEmail: OptionalEmailSchema2,
    primaryPhone: OptionalPhoneE164Schema,
    status: ContactStatusSchema.optional(),
    assignedUserId: z6.string().uuid().nullable().optional(),
    leadSource: z6.string().nullable().optional(),
    notes: z6.string().nullable().optional(),
    internalNotes: z6.string().nullable().optional()
  }).optional(),
  copyContactChannels: z6.boolean().optional()
});
var MergeContactsResultSchema = z6.object({
  winnerContactId: z6.string().uuid(),
  mergedAt: z6.string().datetime(),
  reparentSummary: z6.object({
    leads: z6.number(),
    consolidatedLeads: z6.number(),
    activities: z6.number(),
    appointments: z6.number(),
    tradeIns: z6.number(),
    vehicleInterests: z6.number(),
    followUps: z6.number(),
    contactChannels: z6.number(),
    droppedContactChannels: z6.number(),
    integrationContacts: z6.number(),
    droppedIntegrationContacts: z6.number(),
    consentEvents: z6.number()
  }),
  mergeEventIds: z6.array(z6.string().uuid())
});
var CreateLeadSchema = z6.object({
  dealershipId: z6.string().uuid(),
  contactId: z6.string().uuid().nullable(),
  rooftopId: z6.string().uuid().nullable().optional(),
  primaryChannel: LeadPrimaryChannelSchema,
  channelId: z6.string().uuid().nullable().optional(),
  type: z6.enum(["sales", "service", "parts"]).nullable().optional(),
  status: z6.enum(["new", "working", "appointment", "won", "lost"]).optional(),
  lostReason: z6.string().nullable().optional(),
  interestVehicleId: z6.string().uuid().nullable().optional(),
  interest: z6.string().nullable().optional(),
  closed: z6.boolean().default(false),
  closedAt: z6.string().datetime().nullable().optional(),
  snoozedUntil: z6.string().datetime().nullable().optional(),
  aiAutopilot: z6.boolean().optional(),
  aiSuppressedUntil: z6.string().datetime().nullable().optional(),
  assignedUserId: z6.string().uuid().nullable().optional(),
  lastActivityAt: z6.string().datetime().nullable().optional(),
  lastActivityPreview: OptionalTextSchema,
  unreadAt: z6.string().datetime().nullable().optional(),
  slaDueAt: z6.string().datetime().nullable().optional()
});
var UpdateLeadSchema = CreateLeadSchema.omit({
  dealershipId: true,
  contactId: true
}).partial();
var ActivitySchema = z6.object({
  id: z6.string().uuid(),
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid().nullable(),
  leadId: z6.string().uuid().nullable(),
  contactId: z6.string().uuid().nullable(),
  type: z6.string(),
  direction: ActivityDirectionSchema,
  actorType: ActivityActorTypeSchema,
  actorUserId: z6.string().uuid().nullable(),
  channelId: z6.string().uuid().nullable(),
  body: z6.string().nullable(),
  attachments: JsonArraySchema,
  deliveryStatus: DeliveryStatusSchema.nullable(),
  providerMessageId: z6.string().nullable(),
  externalThreadId: z6.string().nullable(),
  summary: z6.string().nullable(),
  // On-demand, LLM-generated summary. Null until an operator clicks
  // "Generate AI summary"; persisted thereafter.
  aiSummary: z6.string().nullable(),
  occurredAt: z6.string().datetime(),
  // Read-receipts (governance §4). Nullable because not every channel
  // supports them (Telegram bots, plain SMS, plain email). Frontend
  // shows ✓ for delivered (deliveryStatus=sent) and ✓✓ for read
  // (readAt set).
  readAt: z6.string().datetime().nullable(),
  readActorType: z6.enum(["contact", "user", "ai", "system"]).nullable(),
  readActorUserId: z6.string().uuid().nullable(),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
var CreateActivitySchema = z6.object({
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid().nullable(),
  leadId: z6.string().uuid().nullable().optional(),
  contactId: z6.string().uuid().nullable(),
  type: z6.string().trim().min(1),
  direction: ActivityDirectionSchema.default("system"),
  actorType: ActivityActorTypeSchema,
  actorUserId: z6.string().uuid().nullable().optional(),
  channelId: z6.string().uuid().nullable().optional(),
  body: z6.string().nullable().optional(),
  attachments: JsonArraySchema.default([]),
  deliveryStatus: DeliveryStatusSchema.nullable().optional(),
  providerMessageId: OptionalTextSchema,
  externalThreadId: OptionalTextSchema,
  summary: OptionalTextSchema,
  occurredAt: z6.string().datetime().optional()
});
var UpdateActivitySchema = CreateActivitySchema.omit({
  dealershipId: true,
  contactId: true
}).partial();
var LeadAssignSchema = z6.object({
  assignedUserId: z6.string().uuid().nullable()
});
var LeadSnoozeSchema = z6.object({
  snoozedUntil: z6.string().datetime().nullable()
});
var InternalNoteSchema = z6.object({
  body: z6.string().trim().min(1)
});
var OutboundAttachmentKindSchema = z6.enum(["image", "audio", "video", "document"]);
var TranscribeAttachmentSchema = z6.object({
  activityId: z6.string().uuid(),
  attachmentIndex: z6.number().int().nonnegative().max(99)
});
var TranscribeAttachmentResultSchema = z6.object({
  transcript: z6.string()
});
var OutboundAttachmentSchema = z6.object({
  storage: z6.enum(["private", "public"]),
  key: z6.string().min(1).max(512),
  kind: OutboundAttachmentKindSchema,
  mimeType: z6.string().min(1).max(120),
  fileName: z6.string().min(1).max(255).nullable().optional(),
  sizeBytes: z6.number().int().nonnegative().optional(),
  // Audio-only metadata. Optional everywhere — image/video/document
  // attachments keep these null. `durationSec` lets the UI render the
  // Telegram-style mm:ss badge without probing the file; `transcript`
  // is populated lazily on first /attachments/transcribe call and
  // then cached on the attachment row.
  durationSec: z6.number().int().nonnegative().nullable().optional(),
  transcript: z6.string().nullable().optional()
});
var SendLeadMessageSchema = z6.object({
  body: z6.string().trim().max(4e3).optional().default(""),
  attachments: z6.array(OutboundAttachmentSchema).max(10).optional().default([])
}).refine(
  (input) => input.body.trim().length > 0 || (input.attachments?.length ?? 0) > 0,
  {
    message: "Message must include text or at least one attachment.",
    path: ["body"]
  }
);
var StartConversationSchema = z6.object({
  channel: z6.enum(["sms", "email"]),
  // Optional rooftop hint; the server prefers a rooftop-scoped channel and
  // falls back to a dealership-wide one.
  rooftopId: z6.string().uuid().nullable().optional(),
  /**
   * Explicit channel choice (the compose dialog's "send via" picker when
   * the dealership has several active channels of this type). Validated
   * server-side: must be an ACTIVE channel of the matching type in this
   * dealership. Absent = the server resolves one.
   */
  channelId: z6.string().uuid().optional(),
  // Email only: the outbound subject line. Ignored for SMS.
  subject: z6.string().trim().min(1).max(200).optional(),
  body: z6.string().trim().min(1, "Message can't be empty.").max(4e3)
});
var ResolveOrCreateContactSchema = z6.object({
  primaryPhone: OptionalPhoneE164Schema,
  primaryEmail: OptionalEmailSchema2,
  firstName: z6.string().trim().min(1).max(120).nullable().optional(),
  lastName: z6.string().trim().min(1).max(120).nullable().optional()
}).refine((d) => d.primaryPhone ?? d.primaryEmail, {
  message: "Provide at least a phone or an email.",
  path: ["primaryPhone"]
});
var SuggestedResponseSchema = z6.object({
  text: z6.string()
});
var ALLOWED_ATTACHMENT_MIME = [
  // images
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/heic",
  // audio
  "audio/mpeg",
  "audio/mp4",
  "audio/aac",
  "audio/ogg",
  "audio/opus",
  "audio/wav",
  "audio/webm",
  // video
  "video/mp4",
  "video/quicktime",
  "video/webm",
  // documents
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/csv"
];
var CreateAttachmentUploadUrlSchema = z6.object({
  fileName: z6.string().trim().min(1).max(255),
  mimeType: z6.enum(ALLOWED_ATTACHMENT_MIME),
  sizeBytes: z6.number().int().positive().max(25 * 1024 * 1024, "Attachments must be 25 MB or smaller.")
});
var AttachmentUploadUrlResultSchema = z6.object({
  uploadUrl: z6.string().url(),
  /** Storage tier the dispatcher later resolves a delivery URL against. */
  storage: z6.enum(["private", "public"]),
  /** S3 key to send back in `attachments[i].key`. */
  key: z6.string(),
  /** Same MIME the caller supplied — echoed for convenience. */
  mimeType: z6.string(),
  /** ISO timestamp the signed URL expires; the client should not retry past this. */
  expiresAt: z6.string().datetime()
});
var AppointmentSchema = z6.object({
  id: z6.string().uuid(),
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid(),
  contactId: z6.string().uuid(),
  leadId: z6.string().uuid().nullable(),
  appointmentType: AppointmentTypeSchema,
  status: AppointmentStatusSchema,
  scheduledStartAt: z6.string().datetime(),
  durationMinutes: z6.number().int(),
  assignedUserId: z6.string().uuid().nullable(),
  transportType: AppointmentTransportTypeSchema.nullable(),
  // Per-rooftop catalog entry; null for legacy rows and custom one-offs.
  serviceTypeId: z6.string().uuid().nullable(),
  // Sales link: which inventory interest this appointment is about.
  // Service appointments keep using vehicleVin (the customer's own car).
  vehicleInterestId: z6.string().uuid().nullable(),
  vehicleVin: z6.string().nullable(),
  notes: z6.string().nullable(),
  externalStatus: z6.string().nullable(),
  externalId: z6.string().nullable(),
  // TTL for `pending_customer` reservations; null outside that state.
  // Server-managed (set by the AI propose flow, cleared on confirm).
  reservationExpiresAt: z6.string().datetime().nullable(),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
var CreateAppointmentSchema = z6.object({
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid(),
  contactId: z6.string().uuid(),
  leadId: z6.string().uuid().nullable().optional(),
  appointmentType: AppointmentTypeSchema,
  status: AppointmentStatusSchema.default("unconfirmed"),
  scheduledStartAt: z6.string().datetime(),
  durationMinutes: z6.number().int().positive().default(30),
  assignedUserId: z6.string().uuid().nullable().optional(),
  transportType: AppointmentTransportTypeSchema.nullable().optional(),
  serviceTypeId: z6.string().uuid().nullable().optional(),
  vehicleInterestId: z6.string().uuid().nullable().optional(),
  vehicleVin: OptionalVinSchema,
  notes: z6.string().nullable().optional(),
  externalStatus: OptionalTextSchema,
  externalId: OptionalTextSchema
});
var UpdateAppointmentSchema = CreateAppointmentSchema.omit({
  dealershipId: true,
  rooftopId: true,
  contactId: true
}).partial();
var ServiceTypeCategorySchema = z6.enum([
  "sales",
  "service",
  "parts",
  "delivery",
  "bodyshop"
]);
var ReminderStepSchema = z6.object({
  offsetMinutes: z6.number().int(),
  channel: z6.enum(["sms", "email", "call"])
});
var ReminderTemplateSchema = z6.object({
  confirm: ReminderStepSchema.optional(),
  remind: ReminderStepSchema.optional(),
  noShow: ReminderStepSchema.optional()
});
var DEFAULT_REMINDER_TEMPLATE = {
  confirm: { offsetMinutes: -24 * 60, channel: "sms" },
  remind: { offsetMinutes: -2 * 60, channel: "sms" },
  noShow: { offsetMinutes: 60, channel: "sms" }
};
var ServiceTypeSchema = z6.object({
  id: z6.string().uuid(),
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid(),
  name: z6.string(),
  slug: z6.string(),
  category: ServiceTypeCategorySchema,
  defaultDurationMinutes: z6.number().int().positive(),
  bufferBeforeMinutes: z6.number().int().nonnegative(),
  bufferAfterMinutes: z6.number().int().nonnegative(),
  reminderTemplate: ReminderTemplateSchema,
  customerLabel: z6.string().nullable(),
  active: z6.boolean(),
  sortOrder: z6.number().int(),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
var CreateServiceTypeSchema = z6.object({
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid(),
  name: z6.string().trim().min(1).max(120),
  slug: z6.string().trim().regex(/^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/, "lowercase kebab-case"),
  category: ServiceTypeCategorySchema,
  defaultDurationMinutes: z6.number().int().positive().max(24 * 60),
  bufferBeforeMinutes: z6.number().int().nonnegative().max(8 * 60).default(0),
  bufferAfterMinutes: z6.number().int().nonnegative().max(8 * 60).default(0),
  reminderTemplate: ReminderTemplateSchema.default(DEFAULT_REMINDER_TEMPLATE),
  customerLabel: OptionalTextSchema,
  active: z6.boolean().default(true),
  sortOrder: z6.number().int().default(100)
});
var UpdateServiceTypeSchema = CreateServiceTypeSchema.omit({
  dealershipId: true,
  rooftopId: true,
  slug: true
}).partial();
var TelephonyBusinessTypeSchema = z6.enum([
  "LLC",
  "CORPORATION",
  "PARTNERSHIP",
  "NON_PROFIT",
  "SOLE_PROPRIETOR"
]);
var TelephonyBrandStatusSchema = z6.enum([
  "NOT_STARTED",
  "DRAFT",
  "PENDING_OTP",
  "IN_REVIEW",
  "APPROVED",
  "FAILED"
]);
var TelephonyAccountSchema = z6.object({
  id: z6.string().uuid(),
  displayName: z6.string(),
  legalBusinessName: z6.string().nullable(),
  einLast4: z6.string().nullable(),
  businessAddressLine1: z6.string().nullable(),
  businessAddressLine2: z6.string().nullable(),
  businessCity: z6.string().nullable(),
  businessState: z6.string().nullable(),
  businessPostalCode: z6.string().nullable(),
  businessCountry: z6.string(),
  websiteUrl: z6.string().nullable(),
  businessType: TelephonyBusinessTypeSchema.nullable(),
  businessIndustry: z6.string(),
  twilioAccountSid: z6.string(),
  // Voice TwiML Application SID — routing target for outbound Voice
  // SDK calls. Provisioned alongside the subaccount; null only on
  // legacy rows that pre-date the per-account TwiML App rollout.
  twilioVoiceTwimlAppSid: z6.string().nullable(),
  // Trust Hub bundle SIDs + Brand SID — null until the A2P wizard
  // creates them. Surfaced so the UI can show "Submit Brand
  // Registration" vs "Resubmit" etc.
  twilioCustomerProfileSid: z6.string().nullable(),
  twilioA2pTrustProductSid: z6.string().nullable(),
  twilioBrandSid: z6.string().nullable(),
  brandStatus: TelephonyBrandStatusSchema,
  brandTrustScore: z6.number().int().nullable(),
  brandFailureReasons: z6.array(z6.unknown()).nullable(),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
var CreateTelephonyAccountSchema = z6.object({
  displayName: z6.string().trim().min(1).max(120),
  // Optional at creation — admin can fill these later as part of the
  // A2P wizard. Validated at submission time, not here.
  legalBusinessName: z6.string().trim().min(1).max(255).optional(),
  ein: z6.string().trim().regex(/^\d{2}-?\d{7}$/u, "EIN must look like 12-3456789.").optional(),
  businessAddressLine1: z6.string().trim().max(255).optional(),
  businessAddressLine2: z6.string().trim().max(255).optional(),
  businessCity: z6.string().trim().max(120).optional(),
  businessState: UsStateCodeSchema.optional(),
  businessPostalCode: UsZipSchema.optional(),
  businessCountry: CountryCodeSchema.default("US"),
  websiteUrl: z6.string().trim().url().max(255).optional(),
  businessType: TelephonyBusinessTypeSchema.optional()
});
var UpdateTelephonyAccountSchema = CreateTelephonyAccountSchema.partial();
var TelephonyNumberTypeSchema = z6.enum(["LOCAL", "TOLL_FREE"]);
var TelephonyEmergencyStatusSchema = z6.enum(["ACTIVE", "INACTIVE"]);
var TelephonyPhoneNumberSchema = z6.object({
  id: z6.string().uuid(),
  twilioAccountId: z6.string().uuid(),
  rooftopId: z6.string().uuid().nullable(),
  twilioPhoneNumberSid: z6.string(),
  phoneNumberE164: z6.string(),
  phoneNumberFriendly: z6.string().nullable(),
  numberType: TelephonyNumberTypeSchema,
  capabilities: JsonObjectSchema,
  voiceEnabled: z6.boolean(),
  smsEnabled: z6.boolean(),
  emergencyAddressSid: z6.string().nullable(),
  emergencyStatus: TelephonyEmergencyStatusSchema,
  voiceUrl: z6.string().nullable(),
  smsUrl: z6.string().nullable(),
  messagingServiceSid: z6.string().nullable(),
  purchasedAt: z6.string().datetime().nullable(),
  releasedAt: z6.string().datetime().nullable(),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
var TelephonyAccountListSchema = z6.array(TelephonyAccountSchema);
var TelephonyPhoneNumberListSchema = z6.array(TelephonyPhoneNumberSchema);
var TelephonyAccountSearchResultSchema = z6.object({
  id: z6.string().uuid(),
  displayName: z6.string(),
  legalBusinessName: z6.string().nullable(),
  twilioAccountSid: z6.string(),
  brandStatus: TelephonyBrandStatusSchema,
  connectedDealerships: z6.array(
    z6.object({ id: z6.string().uuid(), name: z6.string() })
  )
});
var TelephonyAccountSearchResultListSchema = z6.array(
  TelephonyAccountSearchResultSchema
);
var TelephonyAvailableNumberSchema = z6.object({
  phoneNumber: z6.string(),
  friendlyName: z6.string().nullable(),
  locality: z6.string().nullable(),
  region: z6.string().nullable(),
  postalCode: z6.string().nullable(),
  isoCountry: z6.string(),
  capabilities: z6.object({
    voice: z6.boolean().default(true),
    sms: z6.boolean().default(false),
    mms: z6.boolean().default(false)
  })
});
var TelephonyAvailableNumberListSchema = z6.array(TelephonyAvailableNumberSchema);
var TelephonySearchNumbersSchema = z6.object({
  countryCode: z6.string().trim().min(2).max(2).default("US"),
  areaCode: z6.string().trim().regex(/^\d{3}$/u).optional(),
  contains: z6.string().trim().min(1).max(20).optional(),
  locality: z6.string().trim().min(1).max(80).optional(),
  region: UsStateCodeSchema.optional(),
  numberType: TelephonyNumberTypeSchema.default("LOCAL"),
  limit: z6.number().int().min(1).max(30).default(10)
});
var TelephonyE911AddressSchema = z6.object({
  friendlyName: z6.string().trim().min(1).max(64),
  customerName: z6.string().trim().min(1).max(120),
  street: z6.string().trim().min(1).max(255),
  city: z6.string().trim().min(1).max(80),
  region: UsStateCodeSchema,
  postalCode: UsZipSchema,
  isoCountry: CountryCodeSchema.default("US")
});
var TelephonyPurchaseNumberSchema = z6.object({
  phoneNumber: PhoneE164Schema,
  rooftopId: z6.string().uuid().nullable().optional(),
  emergencyAddress: TelephonyE911AddressSchema
});
var TelephonyUpdateNumberSchema = z6.object({
  rooftopId: z6.string().uuid().nullable(),
  voiceUrl: z6.string().url().nullable(),
  smsUrl: z6.string().url().nullable(),
  phoneNumberFriendly: z6.string().trim().min(1).max(120).nullable()
}).partial();
var A2pBusinessProfileSchema = z6.object({
  legalBusinessName: z6.string().trim().min(1).max(255).nullable(),
  ein: z6.string().trim().regex(/^\d{2}-?\d{7}$/u, "EIN must look like 12-3456789.").nullable(),
  businessAddressLine1: z6.string().trim().max(255).nullable(),
  businessAddressLine2: z6.string().trim().max(255).nullable(),
  businessCity: z6.string().trim().max(120).nullable(),
  businessState: UsStateCodeSchema.nullable(),
  businessPostalCode: UsZipSchema.nullable(),
  businessCountry: CountryCodeSchema,
  websiteUrl: z6.string().trim().url().max(255).nullable(),
  businessType: TelephonyBusinessTypeSchema.nullable()
}).partial();
var A2pBrandTypeSchema = z6.enum([
  "STANDARD",
  "SOLE_PROPRIETOR",
  "LOW_VOLUME_STANDARD"
]);
var A2pJobPositionSchema = z6.enum([
  "Director",
  "GM",
  "VP",
  "CEO",
  "CFO",
  "General Counsel",
  "Other"
]);
var A2pSubmitBrandSchema = z6.object({
  authorizedRepFirstName: z6.string().trim().min(1).max(120),
  authorizedRepLastName: z6.string().trim().min(1).max(120),
  authorizedRepEmail: z6.string().trim().email(),
  // E.164 — Twilio rejects the profile otherwise.
  authorizedRepPhone: z6.string().trim().regex(/^\+[1-9]\d{6,14}$/, "Use E.164 format, e.g. +15551234567."),
  authorizedRepBusinessTitle: z6.string().trim().min(1).max(120),
  authorizedRepJobPosition: A2pJobPositionSchema,
  brandType: A2pBrandTypeSchema.default("STANDARD")
});
var A2pVerifyOtpSchema = z6.object({
  otp: z6.string().trim().regex(/^\d{4,8}$/u, "OTP must be 4\u20138 digits.")
});
var A2pCampaignUseCaseSchema = z6.enum([
  "CUSTOMER_CARE",
  "MARKETING",
  "MIXED",
  "LOW_VOLUME",
  "ACCOUNT_NOTIFICATION",
  "DELIVERY_NOTIFICATION"
]);
var A2pSubmitCampaignSchema = z6.object({
  useCase: A2pCampaignUseCaseSchema.default("CUSTOMER_CARE"),
  description: z6.string().trim().min(40).max(1024),
  messageSamples: z6.array(z6.string().trim().min(20).max(1024)).min(2).max(5),
  messageFlow: z6.string().trim().min(40).max(2048),
  optInMessage: z6.string().trim().min(20).max(320).optional(),
  optOutMessage: z6.string().trim().min(20).max(320).optional(),
  helpMessage: z6.string().trim().min(20).max(320).optional(),
  hasEmbeddedLinks: z6.boolean().default(false),
  hasEmbeddedPhone: z6.boolean().default(false)
});
var A2pCampaignStatusSchema = z6.enum([
  "DRAFT",
  "PENDING",
  "IN_PROGRESS",
  "VERIFIED",
  "FAILED"
]);
var A2pStatusSchema = z6.object({
  telephonyAccountId: z6.string().uuid(),
  brandStatus: TelephonyBrandStatusSchema,
  brandTrustScore: z6.number().int().nullable(),
  brandFailureReasons: z6.array(z6.string()),
  hasMessagingService: z6.boolean(),
  campaignStatus: A2pCampaignStatusSchema,
  campaignSid: z6.string().nullable(),
  campaignFailureReasons: z6.array(z6.string())
});
var TelephonyUsageRowSchema = z6.object({
  day: z6.string(),
  category: z6.string(),
  usageUnit: z6.string(),
  usageCount: z6.number(),
  priceUsd: z6.number()
});
var TelephonyUsageSummarySchema = z6.object({
  telephonyAccountId: z6.string().uuid().nullable(),
  dealershipId: z6.string().uuid().nullable(),
  start: z6.string(),
  end: z6.string(),
  rows: z6.array(TelephonyUsageRowSchema),
  totals: z6.object({
    voiceMinutes: z6.number(),
    smsSegments: z6.number(),
    mmsSegments: z6.number(),
    priceUsd: z6.number()
  })
});
var TelephonyUsageRangeSchema = z6.object({
  start: z6.string().regex(/^\d{4}-\d{2}-\d{2}$/u, "Use YYYY-MM-DD"),
  end: z6.string().regex(/^\d{4}-\d{2}-\d{2}$/u, "Use YYYY-MM-DD")
});
var HexColorSchema = z6.string().trim().regex(/^#[0-9a-fA-F]{6}$/u, "Must be a hex color like #3b82f6").transform((v) => v.toLowerCase());
var TagSchema = z6.object({
  id: z6.string().uuid(),
  dealershipId: z6.string().uuid(),
  slug: z6.string(),
  label: z6.string(),
  color: z6.string().nullable(),
  description: z6.string().nullable(),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
var TagSlugSchema = z6.string().trim().min(1).max(60).regex(/^[a-z0-9-]+$/, "Lowercase letters, numbers, and dashes only.");
var TagLabelSchema = z6.string().trim().min(1).max(60);
function slugFromLabel(label) {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60).replace(/-+$/g, "");
}
var CreateTagSchema = z6.object({
  dealershipId: z6.string().uuid(),
  slug: TagSlugSchema,
  label: TagLabelSchema,
  color: HexColorSchema.nullable().optional(),
  description: OptionalTextSchema
});
var UpdateTagSchema = z6.object({
  slug: TagSlugSchema,
  label: TagLabelSchema,
  color: HexColorSchema.nullable(),
  description: OptionalTextSchema
}).partial();
var ContactTagSchema = z6.object({
  id: z6.string().uuid(),
  dealershipId: z6.string().uuid(),
  contactId: z6.string().uuid(),
  tagId: z6.string().uuid(),
  appliedByUserId: z6.string().uuid().nullable(),
  appliedAt: z6.string().datetime()
});
var CreateContactTagSchema = z6.object({
  dealershipId: z6.string().uuid(),
  contactId: z6.string().uuid(),
  tagId: z6.string().uuid()
});
var CannedResponseSchema = z6.object({
  id: z6.string().uuid(),
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid().nullable(),
  shortcut: z6.string(),
  body: z6.string(),
  isActive: z6.boolean(),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
var CreateCannedResponseSchema = z6.object({
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid().nullable().optional(),
  shortcut: z6.string().trim().min(1),
  body: z6.string().trim().min(1),
  isActive: z6.boolean().default(true)
});
var FollowUpKindSchema = z6.enum(["call", "sms", "email", "task"]);
var FollowUpStatusSchema = z6.enum(["pending", "done", "snoozed", "cancelled"]);
var FollowUpSchema = z6.object({
  id: z6.string().uuid(),
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid().nullable(),
  contactId: z6.string().uuid().nullable(),
  leadId: z6.string().uuid().nullable(),
  // Nullable: an escalation with no human available stays unassigned
  // rather than being parked on the AI (AI Task Management, Part 1).
  assignedUserId: z6.string().uuid().nullable(),
  dueAt: z6.string().datetime(),
  kind: FollowUpKindSchema,
  reason: z6.string().nullable(),
  body: z6.string().nullable(),
  status: FollowUpStatusSchema,
  completedAt: z6.string().datetime().nullable(),
  completedByUserId: z6.string().uuid().nullable(),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
var CreateFollowUpSchema = z6.object({
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid().nullable(),
  contactId: z6.string().uuid(),
  leadId: z6.string().uuid().nullable().optional(),
  assignedUserId: z6.string().uuid(),
  dueAt: z6.string().datetime(),
  kind: FollowUpKindSchema,
  reason: OptionalTextSchema,
  body: OptionalTextSchema,
  status: FollowUpStatusSchema.default("pending")
});
var AiAgentStatusSchema = z6.enum(["draft", "active", "paused"]);
var AiAgentRespondToSchema = z6.enum([
  "assigned_only",
  "assigned_and_unassigned",
  "rooftop_all",
  "group_all"
]);
var AiAgentResponseSplitModeSchema = z6.enum(["single", "multiple"]);
var AiAgentSchema = z6.object({
  id: z6.string().uuid(),
  dealershipId: z6.string().uuid(),
  userId: z6.string().uuid(),
  name: z6.string(),
  description: z6.string().nullable(),
  model: z6.string(),
  temperature: z6.number().nullable(),
  /** Dealership-wide Knowledge & Behavior (was "system prompt"). */
  knowledgeAndBehavior: z6.string(),
  /** Layer the code-owned base BDC-manager persona beneath the K&B. */
  useBasePrompt: z6.boolean(),
  persona: JsonObjectSchema.nullable(),
  config: JsonObjectSchema.nullable(),
  status: AiAgentStatusSchema,
  isStreaming: z6.boolean(),
  allowInternet: z6.boolean(),
  responseDelaySeconds: z6.number().int().min(0),
  defaultSnoozeSeconds: z6.number().int().min(0),
  // "When does the AI respond?" — three assignment-keyed delays (sec).
  replyDelayAssignedSeconds: z6.number().int().min(0),
  replyDelayUnassignedSeconds: z6.number().int().min(0),
  replyDelayOtherAssigneeSeconds: z6.number().int().min(0),
  // Silence period after a human takeover / escalation (seconds).
  aiSuppressionSeconds: z6.number().int().min(0),
  respondTo: AiAgentRespondToSchema,
  responseSplitMode: AiAgentResponseSplitModeSchema,
  // Voice config — admin-only. Null means "use env defaults."
  voiceListenModel: z6.string().nullable(),
  voiceSpeakModel: z6.string().nullable(),
  voiceThinkProvider: z6.string().nullable(),
  voiceThinkModel: z6.string().nullable(),
  // Per-channel persona + tool segregation — admin-only. The Phone
  // (voice) and SMS channels each carry their own K&B overlay
  // (appended on top of the shared knowledgeAndBehavior) and their own
  // tool exclusion list. Null = no overlay / code-default tools.
  voicePrompt: z6.string().nullable(),
  smsPrompt: z6.string().nullable(),
  voiceExcludedTools: z6.array(z6.string()).nullable(),
  smsExcludedTools: z6.array(z6.string()).nullable(),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
var CreateAiAgentSchema = z6.object({
  dealershipId: z6.string().uuid(),
  userId: z6.string().uuid(),
  name: z6.string().trim().min(1).max(120),
  description: OptionalTextSchema,
  // Must be "<provider>:<modelId>" — the contract parseModelString
  // enforces at run time (@lumika/ai-agent). Guarding here turns a
  // silently-broken agent into a 400 at the API boundary instead of a
  // swallowed run-time WARN. We validate only the STRUCTURAL shape
  // (non-empty provider, ':', non-empty modelId), mirroring
  // parseModelString's own rule. The provider allowlist
  // (SUPPORTED_PROVIDERS) stays single-sourced in @lumika/ai-agent —
  // packages/shared cannot import that ESM-only package, and it is
  // still enforced at run time; duplicating the list here would risk
  // drift.
  model: z6.string().trim().regex(
    /^[^\s:]+:.+$/,
    'model must be "<provider>:<modelId>" (e.g. "openai:gpt-4o", "anthropic:claude-sonnet-4-5")'
  ),
  // 0..1 is the one range every provider pi-ai routes to accepts —
  // anthropic-messages endpoints (incl. Fireworks-hosted models) hard
  // 400 on temperature > 1. The knob used to be silently dropped, so
  // unbounded garbage was harmless; now that it reaches the provider
  // request, reject it at the API boundary. The runtime additionally
  // clamps legacy out-of-range rows (see @lumika/ai-agent buildAgent).
  temperature: z6.number().min(0).max(1).nullable().optional(),
  knowledgeAndBehavior: z6.string().trim().min(1).max(2e4),
  useBasePrompt: z6.boolean().default(true),
  persona: JsonObjectSchema.nullable().optional(),
  config: JsonObjectSchema.nullable().optional(),
  status: AiAgentStatusSchema.default("draft"),
  isStreaming: z6.boolean().default(false),
  allowInternet: z6.boolean().default(false),
  responseDelaySeconds: z6.number().int().min(0).default(0),
  defaultSnoozeSeconds: z6.number().int().min(0).default(0),
  // Defaults: respond now when the AI owns it, wait 5min when nobody
  // owns it, wait 15min when a teammate owns it.
  replyDelayAssignedSeconds: z6.number().int().min(0).default(0),
  replyDelayUnassignedSeconds: z6.number().int().min(0).default(300),
  replyDelayOtherAssigneeSeconds: z6.number().int().min(0).default(900),
  // Default 30 days — a deliberate, long hand-off once a human steps in.
  aiSuppressionSeconds: z6.number().int().min(0).default(2592e3),
  respondTo: AiAgentRespondToSchema.default("assigned_and_unassigned"),
  responseSplitMode: AiAgentResponseSplitModeSchema.default("multiple"),
  voiceListenModel: z6.string().trim().min(1).nullable().optional(),
  voiceSpeakModel: z6.string().trim().min(1).nullable().optional(),
  voiceThinkProvider: z6.string().trim().min(1).nullable().optional(),
  voiceThinkModel: z6.string().trim().min(1).nullable().optional(),
  // Per-channel persona + tool segregation — admin-only. Empty/omitted
  // means no overlay (voice/sms run with shared K&B + code-default
  // tools); a tool-exclusion array (including []) is honoured verbatim.
  voicePrompt: z6.string().trim().max(2e4).nullable().optional(),
  smsPrompt: z6.string().trim().max(2e4).nullable().optional(),
  voiceExcludedTools: z6.array(z6.string().trim().min(1)).nullable().optional(),
  smsExcludedTools: z6.array(z6.string().trim().min(1)).nullable().optional()
});
var UpdateAiAgentSchema = CreateAiAgentSchema.pick({
  name: true,
  description: true,
  model: true,
  temperature: true,
  knowledgeAndBehavior: true,
  useBasePrompt: true,
  persona: true,
  config: true,
  status: true,
  isStreaming: true,
  allowInternet: true,
  responseDelaySeconds: true,
  defaultSnoozeSeconds: true,
  replyDelayAssignedSeconds: true,
  replyDelayUnassignedSeconds: true,
  replyDelayOtherAssigneeSeconds: true,
  aiSuppressionSeconds: true,
  respondTo: true,
  responseSplitMode: true,
  voiceListenModel: true,
  voiceSpeakModel: true,
  voiceThinkProvider: true,
  voiceThinkModel: true,
  voicePrompt: true,
  smsPrompt: true,
  voiceExcludedTools: true,
  smsExcludedTools: true
}).partial();
var SkillContentSchema = z6.string().trim().min(200, "Skill content must be at least 200 characters.");
var AiSkillSchema = z6.object({
  id: z6.string().uuid(),
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid().nullable(),
  name: z6.string(),
  description: z6.string().nullable(),
  content: z6.string(),
  isEnabled: z6.boolean(),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
var CreateAiSkillSchema = z6.object({
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid().nullable().optional(),
  name: z6.string().trim().min(1).max(120),
  description: OptionalTextSchema,
  content: SkillContentSchema,
  isEnabled: z6.boolean().default(true)
});
var UpdateAiSkillSchema = z6.object({
  rooftopId: z6.string().uuid().nullable(),
  name: z6.string().trim().min(1),
  description: OptionalTextSchema,
  content: SkillContentSchema,
  isEnabled: z6.boolean()
}).partial();
var AiRooftopPromptSchema = z6.object({
  id: z6.string().uuid(),
  aiAgentId: z6.string().uuid(),
  rooftopId: z6.string().uuid(),
  knowledgeAndBehavior: z6.string(),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
var AiRooftopPromptListSchema = z6.array(AiRooftopPromptSchema);
var UpsertAiRooftopPromptSchema = z6.object({
  rooftopId: z6.string().uuid(),
  knowledgeAndBehavior: z6.string().trim().min(1).max(2e4)
});
var AiAgentChannelAssignmentSchema = z6.object({
  id: z6.string().uuid(),
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid(),
  aiAgentId: z6.string().uuid(),
  channelId: z6.string().uuid(),
  medium: z6.string().nullable(),
  isPrimary: z6.boolean(),
  priority: z6.number().int().nullable(),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
var AiAssignmentMediumSchema = z6.enum(["sms", "voice"]);
var CreateAiAgentChannelAssignmentSchema = z6.object({
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid(),
  aiAgentId: z6.string().uuid(),
  channelId: z6.string().uuid(),
  medium: AiAssignmentMediumSchema.nullable().optional(),
  isPrimary: z6.boolean().default(false),
  priority: z6.number().int().nullable().optional()
});
var AiAgentSkillSchema = z6.object({
  id: z6.string().uuid(),
  dealershipId: z6.string().uuid(),
  aiAgentId: z6.string().uuid(),
  aiSkillId: z6.string().uuid(),
  createdAt: z6.string().datetime()
});
var CreateAiAgentSkillSchema = z6.object({
  aiSkillId: z6.string().uuid()
});
var AiAgentDetailSchema = z6.object({
  agent: AiAgentSchema,
  channelAssignments: z6.array(AiAgentChannelAssignmentSchema),
  skillGrants: z6.array(AiAgentSkillSchema)
});
var AiAgentDispatchSkipReasonSchema = z6.enum([
  "agent_not_active",
  "conversation_closed",
  "contact_opted_out",
  "ai_autopilot_off",
  "ai_suppressed_window",
  "channel_inactive",
  "routing_rule",
  "human_already_responded"
]);
var AiAgentDispatchDecisionSchema = z6.object({
  shouldRespond: z6.boolean(),
  scheduleAt: z6.string().datetime(),
  skipReason: AiAgentDispatchSkipReasonSchema.nullable()
});
var AiAgentDispatchCheckRequestSchema = z6.object({
  leadId: z6.string().uuid()
});
var VehicleInterestLevelSchema = z6.enum([
  "casual",
  "considering",
  "ready_to_buy",
  "lost"
]);
var VehicleInterestStatusSchema = z6.enum(["open", "converted", "lost", "dropped"]);
var VehicleInterestSchema = z6.object({
  id: z6.string().uuid(),
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid().nullable(),
  contactId: z6.string().uuid(),
  inventoryVehicleId: z6.string().uuid().nullable(),
  vin: z6.string().nullable(),
  year: z6.number().int().nullable(),
  make: z6.string().nullable(),
  model: z6.string().nullable(),
  trim: z6.string().nullable(),
  interestLevel: VehicleInterestLevelSchema.nullable(),
  firstMentionedAt: z6.string().datetime().nullable(),
  lastMentionedAt: z6.string().datetime().nullable(),
  leadId: z6.string().uuid().nullable(),
  status: VehicleInterestStatusSchema,
  notes: z6.string().nullable(),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
var CreateVehicleInterestSchema = z6.object({
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid().nullable(),
  contactId: z6.string().uuid(),
  inventoryVehicleId: z6.string().uuid().nullable().optional(),
  vin: OptionalTextSchema,
  year: z6.number().int().nullable().optional(),
  make: OptionalTextSchema,
  model: OptionalTextSchema,
  trim: OptionalTextSchema,
  interestLevel: VehicleInterestLevelSchema.nullable().optional(),
  firstMentionedAt: z6.string().datetime().nullable().optional(),
  lastMentionedAt: z6.string().datetime().nullable().optional(),
  leadId: z6.string().uuid().nullable().optional(),
  status: VehicleInterestStatusSchema.default("open"),
  notes: OptionalTextSchema
});
var ContactVehicleRelationshipSchema = z6.enum([
  "owns",
  "leases",
  "previously_owned"
]);
var ContactVehicleSchema = z6.object({
  id: z6.string().uuid(),
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid().nullable(),
  contactId: z6.string().uuid(),
  vin: z6.string().nullable(),
  year: z6.number().int().nullable(),
  make: z6.string().nullable(),
  model: z6.string().nullable(),
  trim: z6.string().nullable(),
  color: z6.string().nullable(),
  mileage: z6.number().int().nullable(),
  relationship: ContactVehicleRelationshipSchema,
  purchasedFromThisDealer: z6.boolean().nullable(),
  purchaseDate: DateSchema.nullable(),
  lastServiceAt: z6.string().datetime().nullable(),
  nextServiceDueAt: z6.string().datetime().nullable(),
  source: z6.string().nullable(),
  notes: z6.string().nullable(),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
var CreateContactVehicleSchema = z6.object({
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid().nullable(),
  contactId: z6.string().uuid(),
  vin: OptionalTextSchema,
  year: z6.number().int().nullable().optional(),
  make: OptionalTextSchema,
  model: OptionalTextSchema,
  trim: OptionalTextSchema,
  color: OptionalTextSchema,
  mileage: z6.number().int().nullable().optional(),
  relationship: ContactVehicleRelationshipSchema,
  purchasedFromThisDealer: z6.boolean().nullable().optional(),
  purchaseDate: DateSchema.nullable().optional(),
  lastServiceAt: z6.string().datetime().nullable().optional(),
  nextServiceDueAt: z6.string().datetime().nullable().optional(),
  source: OptionalTextSchema,
  notes: OptionalTextSchema
});
var TradeInStatusSchema = z6.enum([
  "submitted",
  "appraising",
  "offered",
  "accepted",
  "rejected",
  "withdrawn"
]);
var TradeInConditionSchema = z6.enum(["excellent", "good", "fair", "poor"]);
var TradeInSchema = z6.object({
  id: z6.string().uuid(),
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid().nullable(),
  contactId: z6.string().uuid(),
  leadId: z6.string().uuid().nullable(),
  vin: z6.string().nullable(),
  year: z6.number().int().nullable(),
  make: z6.string().nullable(),
  model: z6.string().nullable(),
  trim: z6.string().nullable(),
  color: z6.string().nullable(),
  mileage: z6.number().int().nullable(),
  condition: TradeInConditionSchema.nullable(),
  hasAccidents: z6.boolean().nullable(),
  accidentNotes: z6.string().nullable(),
  payoffAmount: z6.number().nullable(),
  payoffLender: z6.string().nullable(),
  expectedValue: z6.number().nullable(),
  offeredValue: z6.number().nullable(),
  appraisedAt: z6.string().datetime().nullable(),
  appraisedByUserId: z6.string().uuid().nullable(),
  kbbValue: z6.number().nullable(),
  blackBookValue: z6.number().nullable(),
  manheimValue: z6.number().nullable(),
  photos: z6.array(z6.string()).nullable(),
  status: TradeInStatusSchema,
  notes: z6.string().nullable(),
  createdAt: z6.string().datetime(),
  updatedAt: z6.string().datetime()
});
var CreateTradeInSchema = z6.object({
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid().nullable(),
  contactId: z6.string().uuid(),
  leadId: z6.string().uuid().nullable().optional(),
  vin: OptionalTextSchema,
  year: z6.number().int().nullable().optional(),
  make: OptionalTextSchema,
  model: OptionalTextSchema,
  trim: OptionalTextSchema,
  color: OptionalTextSchema,
  mileage: z6.number().int().nullable().optional(),
  condition: TradeInConditionSchema.nullable().optional(),
  hasAccidents: z6.boolean().nullable().optional(),
  accidentNotes: OptionalTextSchema,
  payoffAmount: z6.number().nullable().optional(),
  payoffLender: OptionalTextSchema,
  expectedValue: z6.number().nullable().optional(),
  offeredValue: z6.number().nullable().optional(),
  appraisedAt: z6.string().datetime().nullable().optional(),
  appraisedByUserId: z6.string().uuid().nullable().optional(),
  photos: z6.array(z6.string()).nullable().optional(),
  status: TradeInStatusSchema.default("submitted"),
  notes: OptionalTextSchema
});
var ConsentChannelTypeSchema = z6.enum(["sms", "email", "voice"]);
var ConsentEventTypeSchema = z6.enum(["opt_in", "opt_out"]);
var ConsentScopeSchema = z6.enum(["marketing", "transactional", "all"]);
var ConsentSourceSchema = z6.enum([
  "webform_checkbox",
  "reply_keyword",
  "one_click_link",
  "dealer_manual",
  "adf_disclosure",
  "api"
]);
var ConsentEventSchema = z6.object({
  id: z6.string().uuid(),
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid().nullable(),
  contactId: z6.string().uuid(),
  contactChannelId: z6.string().uuid().nullable(),
  channelType: ConsentChannelTypeSchema,
  event: ConsentEventTypeSchema,
  scope: ConsentScopeSchema,
  source: ConsentSourceSchema,
  actorUserId: z6.string().uuid().nullable(),
  evidence: JsonObjectSchema.nullable(),
  occurredAt: z6.string().datetime(),
  createdAt: z6.string().datetime()
});
var CreateConsentEventSchema = z6.object({
  dealershipId: z6.string().uuid(),
  rooftopId: z6.string().uuid().nullable(),
  contactId: z6.string().uuid(),
  contactChannelId: z6.string().uuid().nullable().optional(),
  channelType: ConsentChannelTypeSchema,
  event: ConsentEventTypeSchema,
  scope: ConsentScopeSchema,
  source: ConsentSourceSchema,
  actorUserId: z6.string().uuid().nullable().optional(),
  evidence: JsonObjectSchema.nullable().optional()
});
var ContactListSchema = z6.array(ContactSchema);
var ContactPageSchema = z6.object({
  items: ContactListSchema,
  total: z6.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var ContactChannelListSchema = z6.array(ContactChannelSchema);
var ContactChannelPageSchema = z6.object({
  items: ContactChannelListSchema,
  total: z6.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var InventoryVehicleListSchema = z6.array(InventoryVehicleSchema);
var InventoryVehiclePageSchema = z6.object({
  items: InventoryVehicleListSchema,
  total: z6.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var ChannelListSchema = z6.array(ChannelSchema);
var ChannelPageSchema = z6.object({
  items: ChannelListSchema,
  total: z6.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var LeadListSchema = z6.array(LeadSchema);
var LeadPageSchema = z6.object({
  items: LeadListSchema,
  total: z6.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var LeadFeedSchema = z6.object({
  items: LeadListSchema,
  nextCursor: z6.string().nullable()
});
var LeadBoardColumnSchema = z6.object({
  status: z6.enum(["new", "working", "appointment", "won", "lost"]),
  count: z6.number(),
  leads: LeadListSchema,
  nextCursor: z6.string().nullable()
});
var LeadBoardSchema = z6.object({
  columns: z6.array(LeadBoardColumnSchema)
});
var ActivityListSchema = z6.array(ActivitySchema);
var ActivityPageSchema = z6.object({
  items: ActivityListSchema,
  total: z6.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var AppointmentListSchema = z6.array(AppointmentSchema);
var AppointmentPageSchema = z6.object({
  items: AppointmentListSchema,
  total: z6.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var FollowUpListSchema = z6.array(FollowUpSchema);
var VehicleInterestListSchema = z6.array(VehicleInterestSchema);
var ContactVehicleListSchema = z6.array(ContactVehicleSchema);
var TradeInListSchema = z6.array(TradeInSchema);
var ConsentEventListSchema = z6.array(ConsentEventSchema);
var FollowUpPageSchema = z6.object({
  items: FollowUpListSchema,
  total: z6.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var VehicleInterestPageSchema = z6.object({
  items: VehicleInterestListSchema,
  total: z6.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var ContactVehiclePageSchema = z6.object({
  items: ContactVehicleListSchema,
  total: z6.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var TradeInPageSchema = z6.object({
  items: TradeInListSchema,
  total: z6.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var ConsentEventPageSchema = z6.object({
  items: ConsentEventListSchema,
  total: z6.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var TagListSchema = z6.array(TagSchema);
var ContactTagListSchema = z6.array(ContactTagSchema);
var CannedResponseListSchema = z6.array(CannedResponseSchema);
var AiAgentListSchema = z6.array(AiAgentSchema);
var AiSkillListSchema = z6.array(AiSkillSchema);
var AiAgentChannelAssignmentListSchema = z6.array(AiAgentChannelAssignmentSchema);
var ReportVolumeSchema = z6.object({
  byDay: z6.array(
    z6.object({
      date: z6.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      inbound: z6.number().int().nonnegative(),
      outbound: z6.number().int().nonnegative()
    })
  ),
  byChannel: z6.array(
    z6.object({
      channel: z6.string(),
      count: z6.number().int().nonnegative()
    })
  ),
  byAgent: z6.array(
    z6.object({
      name: z6.string(),
      count: z6.number().int().nonnegative()
    })
  )
});

// src/dto/billing.dto.ts
var AiUsageBreakdownRowSchema = z7.object({
  /** Group key: provider, model, kind, day (YYYY-MM-DD), or dealership uuid. */
  key: z7.string(),
  /** Human label for the key — same as `key` unless a join enriches it. */
  label: z7.string(),
  inputTokens: z7.number(),
  outputTokens: z7.number(),
  cacheReadTokens: z7.number(),
  cacheWriteTokens: z7.number(),
  units: z7.number().nullable(),
  durationMs: z7.number().nullable(),
  costUsd: z7.number(),
  /** True if any row in this group used a fallback price (no exact model match). */
  estimated: z7.boolean(),
  /** True if any row had no price match at all (cost = 0). */
  unpriced: z7.boolean()
});
var AiUsageSummarySchema = z7.object({
  start: z7.string(),
  end: z7.string(),
  totals: z7.object({
    inputTokens: z7.number(),
    outputTokens: z7.number(),
    cacheReadTokens: z7.number(),
    cacheWriteTokens: z7.number(),
    costUsd: z7.number(),
    rows: z7.number()
  }),
  byProvider: z7.array(AiUsageBreakdownRowSchema),
  byModel: z7.array(AiUsageBreakdownRowSchema),
  byKind: z7.array(AiUsageBreakdownRowSchema),
  byDay: z7.array(AiUsageBreakdownRowSchema)
});
var DealershipBillingSummarySchema = z7.object({
  dealershipId: z7.string().uuid(),
  start: z7.string(),
  end: z7.string(),
  ai: AiUsageSummarySchema,
  twilio: TelephonyUsageSummarySchema,
  combined: z7.object({
    totalUsd: z7.number(),
    aiUsd: z7.number(),
    twilioUsd: z7.number()
  })
});
var PlatformBillingSummarySchema = z7.object({
  start: z7.string(),
  end: z7.string(),
  ai: AiUsageSummarySchema,
  /** Top-N dealerships by combined spend in the period. */
  topDealerships: z7.array(
    z7.object({
      dealershipId: z7.string().uuid(),
      dealershipName: z7.string(),
      aiUsd: z7.number(),
      twilioUsd: z7.number(),
      totalUsd: z7.number()
    })
  ),
  totals: z7.object({
    aiUsd: z7.number(),
    twilioUsd: z7.number(),
    totalUsd: z7.number()
  }),
  /**
   * Platform Twilio total minus the sum of distinct attributed account
   * costs. Non-zero when an account has no dealership association (e.g.
   * a shared or orphan subaccount) — surfaced here rather than silently
   * dropped. Floored at 0.
   */
  unattributedTwilioUsd: z7.number(),
  /**
   * Min/max day present in twilio_usage_records within the requested
   * range. Null when there are no records. Used by the UI to flag a
   * coverage gap when the synced span is narrower than the query range.
   */
  twilioCoverage: z7.object({ minDay: z7.string(), maxDay: z7.string() }).nullable()
});
var BillingRangeSchema = z7.object({
  start: z7.string().regex(/^\d{4}-\d{2}-\d{2}$/u, "Use YYYY-MM-DD"),
  end: z7.string().regex(/^\d{4}-\d{2}-\d{2}$/u, "Use YYYY-MM-DD")
});

// src/dto/auth.dto.ts
import { z as z10 } from "zod";

// src/dto/dealership.dto.ts
import { z as z9 } from "zod";

// src/dto/user.dto.ts
import { z as z8 } from "zod";
var UserStatusSchema = z8.enum(["pending", "active", "inactive"]);
var UserKindSchema = z8.enum(["human", "ai"]);
var MembershipRoleSchema = z8.enum([
  "dealership_owner",
  "dealership_admin",
  "sales_manager",
  "service_manager",
  "bdc_agent",
  "sales_agent",
  "service_agent",
  "readonly"
]);
var InboxVisibilitySchema = z8.enum([
  "assigned_only",
  "assigned_and_unassigned",
  "rooftop_all",
  "group_all"
]);
var NullableStringSchema = z8.string().trim().min(1).max(120).nullable().optional();
var UserSchema = z8.object({
  id: z8.string().uuid(),
  dealershipId: z8.string().uuid(),
  email: z8.string().email().nullable(),
  phone: z8.string().nullable(),
  firstName: z8.string().nullable(),
  lastName: z8.string().nullable(),
  displayName: z8.string().nullable(),
  // CDN URL of the re-hosted avatar (S3 public bucket) — never a raw
  // third-party URL.
  avatarUrl: z8.string().nullable(),
  department: z8.string().nullable(),
  jobTitle: z8.string().nullable(),
  kind: UserKindSchema,
  status: UserStatusSchema,
  roundRobinEnabled: z8.boolean(),
  lastAssignedAt: z8.string().datetime().nullable(),
  lastLoginAt: z8.string().datetime().nullable(),
  createdAt: z8.string().datetime(),
  updatedAt: z8.string().datetime()
});
var CreateUserSchema = z8.object({
  dealershipId: z8.string().uuid(),
  email: z8.string().email().optional().nullable(),
  phone: OptionalPhoneE164Schema,
  firstName: NullableStringSchema,
  lastName: NullableStringSchema,
  displayName: NullableStringSchema,
  department: NullableStringSchema,
  jobTitle: NullableStringSchema,
  kind: UserKindSchema.default("human"),
  status: UserStatusSchema.default("pending"),
  roundRobinEnabled: z8.boolean().default(true)
});
var UpdateUserSchema = CreateUserSchema.omit({ dealershipId: true }).partial();
var InviteLocationsSchema = z8.union([
  z8.literal("all"),
  z8.array(z8.string().uuid()).min(1, "Choose at least one location.")
]);
var InviteUserSchema = z8.object({
  email: z8.string().email(),
  firstName: NullableStringSchema,
  lastName: NullableStringSchema,
  displayName: NullableStringSchema,
  jobTitle: NullableStringSchema,
  department: NullableStringSchema,
  phone: OptionalPhoneE164Schema,
  status: UserStatusSchema.default("pending"),
  roundRobinEnabled: z8.boolean().default(true),
  role: MembershipRoleSchema,
  locations: InviteLocationsSchema,
  inboxVisibility: InboxVisibilitySchema.default("assigned_and_unassigned")
});
var UserMembershipSchema = z8.object({
  id: z8.string().uuid(),
  userId: z8.string().uuid(),
  dealershipId: z8.string().uuid(),
  rooftopId: z8.string().uuid().nullable(),
  role: MembershipRoleSchema,
  inboxVisibility: InboxVisibilitySchema,
  permissions: z8.record(z8.unknown()).nullable(),
  createdAt: z8.string().datetime(),
  updatedAt: z8.string().datetime()
});
var CreateUserMembershipSchema = z8.object({
  userId: z8.string().uuid(),
  dealershipId: z8.string().uuid(),
  rooftopId: z8.string().uuid().nullable().optional(),
  role: MembershipRoleSchema,
  inboxVisibility: InboxVisibilitySchema.default("assigned_and_unassigned"),
  permissions: z8.record(z8.unknown()).nullable().optional()
});
var UpdateUserMembershipSchema = CreateUserMembershipSchema.omit({
  userId: true,
  dealershipId: true
}).partial();
var UserListSchema = z8.array(UserSchema);
var UserMembershipListSchema = z8.array(UserMembershipSchema);
var AVATAR_MIME = ["image/jpeg", "image/png", "image/webp", "image/gif"];
var CreateAvatarUploadUrlSchema = z8.object({
  fileName: z8.string().trim().min(1).max(255).optional(),
  mimeType: z8.enum(AVATAR_MIME)
});
var AvatarUploadUrlResultSchema = z8.object({
  uploadUrl: z8.string().url(),
  /** Staging key to send back to POST /users/me/avatar. */
  key: z8.string(),
  /** ISO timestamp the signed URL expires; the client should not retry past this. */
  expiresAt: z8.string().datetime()
});
var SetAvatarSchema = z8.object({
  /** Staging key returned by the upload-url endpoint. */
  key: z8.string().min(1).max(1024)
});

// src/dto/dealership.dto.ts
var DealershipStatusSchema = z9.enum([
  "onboarding",
  "pilot",
  "active",
  "paused",
  "inactive"
]);
var RooftopStatusSchema = z9.enum(["onboarding", "active", "inactive"]);
var DealershipCountrySchema = z9.enum(["US", "CA"]);
var OptionalTextSchema2 = z9.string().trim().min(1).max(200).nullable().optional();
var DealershipSchema = z9.object({
  id: z9.string().uuid(),
  name: z9.string().min(1),
  legalName: z9.string().nullable(),
  ein: z9.string().nullable(),
  status: DealershipStatusSchema,
  // Defaulted so payloads serialized before the country rollout still parse.
  country: DealershipCountrySchema.default("US"),
  primaryTimezone: z9.string().min(1),
  // Whether AI escalations engage the human-handoff workflow + alerts
  // (true) or are recorded silently (false). Defaulted so payloads
  // serialized before this flag shipped still parse.
  escalationsEnabled: z9.boolean().default(false),
  // Defaulted so payloads serialized before archiving shipped still parse.
  // null = live; ISO timestamp = archived since that point.
  archivedAt: z9.string().datetime().nullable().default(null),
  createdAt: z9.string().datetime(),
  updatedAt: z9.string().datetime()
});
var CreateDealershipSchema = z9.object({
  name: z9.string().trim().min(1).max(200),
  legalName: OptionalTextSchema2,
  ein: OptionalEinSchema,
  status: DealershipStatusSchema.default("onboarding"),
  country: DealershipCountrySchema.default("US"),
  primaryTimezone: z9.string().trim().min(1).max(64).default("America/New_York"),
  escalationsEnabled: z9.boolean().default(false)
});
var UpdateDealershipSchema = CreateDealershipSchema.partial();
var RooftopSchema = z9.object({
  id: z9.string().uuid(),
  dealershipId: z9.string().uuid(),
  name: z9.string().min(1),
  status: RooftopStatusSchema,
  websiteUrl: z9.string().url().nullable(),
  primaryEmail: z9.string().email().nullable(),
  primaryPhone: z9.string().nullable(),
  state: z9.string().nullable(),
  city: z9.string().nullable(),
  zip: z9.string().nullable(),
  addressLine1: z9.string().nullable(),
  timezone: z9.string(),
  salesHours: z9.record(z9.unknown()),
  serviceHours: z9.record(z9.unknown()),
  // Per-department "not applicable" flags (inverse: true = this rooftop
  // does NOT run that department). Defaulted so payloads serialized
  // before these flags shipped still parse, and so absent reads as
  // "department exists" — non-breaking for every existing rooftop.
  salesNotApplicable: z9.boolean().default(false),
  serviceNotApplicable: z9.boolean().default(false),
  partsNotApplicable: z9.boolean().default(false),
  createdAt: z9.string().datetime(),
  updatedAt: z9.string().datetime()
});
var CreateRooftopSchema = z9.object({
  dealershipId: z9.string().uuid(),
  name: z9.string().trim().min(1).max(200),
  status: RooftopStatusSchema.default("active"),
  websiteUrl: OptionalUrlSchema,
  primaryEmail: OptionalEmailSchema,
  primaryPhone: OptionalPhoneE164Schema,
  state: OptionalStateOrProvinceCodeSchema,
  city: OptionalTextSchema2,
  zip: OptionalZipOrPostalCodeSchema,
  addressLine1: OptionalTextSchema2,
  timezone: z9.string().trim().min(1).max(64).default("America/New_York"),
  salesHours: z9.record(z9.unknown()).default({}),
  serviceHours: z9.record(z9.unknown()).default({}),
  salesNotApplicable: z9.boolean().default(false),
  serviceNotApplicable: z9.boolean().default(false),
  partsNotApplicable: z9.boolean().default(false)
});
var UpdateRooftopSchema = CreateRooftopSchema.omit({ dealershipId: true }).partial();
var WEEKDAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
];
var DayHoursSchema = z9.object({
  open: z9.boolean(),
  startMinute: z9.number().int().multipleOf(15).min(0).max(1425),
  endMinute: z9.number().int().multipleOf(15).min(15).max(1440)
}).refine((h) => !h.open || h.endMinute > h.startMinute, {
  message: "Closing time must be after the opening time.",
  path: ["endMinute"]
});
var WeeklyBusinessHoursSchema = z9.object({
  monday: DayHoursSchema,
  tuesday: DayHoursSchema,
  wednesday: DayHoursSchema,
  thursday: DayHoursSchema,
  friday: DayHoursSchema,
  saturday: DayHoursSchema,
  sunday: DayHoursSchema
});
var UpdateRooftopScheduleSchema = z9.object({
  timezone: z9.string().trim().min(1).max(64),
  salesHours: WeeklyBusinessHoursSchema,
  serviceHours: WeeklyBusinessHoursSchema,
  salesNotApplicable: z9.boolean().optional(),
  serviceNotApplicable: z9.boolean().optional()
});
var DealershipDetailSchema = DealershipSchema.extend({
  rooftops: z9.array(RooftopSchema),
  users: z9.array(UserSchema),
  memberships: z9.array(UserMembershipSchema)
});
var DealershipListSchema = z9.array(DealershipSchema);
var RooftopListSchema = z9.array(RooftopSchema);
var OnboardingCreateSchema = z9.object({
  dealership: CreateDealershipSchema,
  rooftops: z9.array(CreateRooftopSchema.omit({ dealershipId: true })).min(1),
  users: z9.array(
    CreateUserSchema.omit({ dealershipId: true }).extend({
      role: MembershipRoleSchema.default("dealership_owner"),
      rooftopId: z9.string().uuid().optional(),
      inboxVisibility: InboxVisibilitySchema.default("group_all")
    })
  )
});

// src/dto/auth.dto.ts
var AdminSchema = z10.object({
  id: z10.string().uuid(),
  email: z10.string().email(),
  firstName: z10.string().nullable(),
  lastName: z10.string().nullable(),
  displayName: z10.string().nullable(),
  role: z10.enum(["admin", "readonly"]),
  lastLoginAt: z10.string().datetime().nullable(),
  createdAt: z10.string().datetime(),
  updatedAt: z10.string().datetime()
});
var AccessibleDealershipSchema = z10.object({
  userId: z10.string().uuid(),
  dealershipId: z10.string().uuid(),
  dealershipName: z10.string(),
  current: z10.boolean()
});
var AppMeSchema = z10.object({
  user: UserSchema,
  dealership: DealershipSchema,
  memberships: z10.array(UserMembershipSchema),
  // Defaulted so a frontend that expects the switcher list still parses a
  // response from a backend that predates it (rolling deploy window).
  accessibleDealerships: z10.array(AccessibleDealershipSchema).default([])
});
var DealershipChoiceSchema = z10.object({
  userId: z10.string().uuid(),
  dealershipId: z10.string().uuid(),
  dealershipName: z10.string()
});
var DealershipChoiceListSchema = z10.array(DealershipChoiceSchema);
var SelectDealershipSchema = z10.object({
  dealershipId: z10.string().uuid()
});
var GoogleCallbackSchema = z10.object({
  credential: z10.string().min(1),
  g_csrf_token: z10.string().min(1).optional()
});
var AdminMeSchema = z10.object({
  admin: AdminSchema
});
var RequestEmailCodeSchema = z10.object({
  email: z10.string().email()
});
var VerifyEmailCodeSchema = z10.object({
  email: z10.string().email(),
  /** 8 decimal digits; regex preserves leading zeros as a string. */
  code: z10.string().regex(/^\d{8}$/)
});
var EmailCodeLoginResultSchema = z10.object({
  kind: z10.enum(["session", "choice"])
});

// src/dto/notifications.dto.ts
import { z as z11 } from "zod";
var NotificationSeveritySchema = z11.enum(["escalation", "normal"]);
var NotificationCategorySchema = z11.enum([
  "ai",
  "engagement",
  "appointment",
  "followup",
  "mention",
  "channel",
  "task"
]);
var NotificationEventTypeSchema = z11.object({
  key: z11.string(),
  category: NotificationCategorySchema,
  severity: NotificationSeveritySchema,
  description: z11.string(),
  defaultInapp: z11.boolean()
});
var NotificationSchema = z11.object({
  id: z11.string().uuid(),
  dealershipId: z11.string().uuid(),
  userId: z11.string().uuid(),
  eventKey: z11.string(),
  title: z11.string(),
  body: z11.string(),
  payload: z11.record(z11.string(), z11.unknown()),
  sourceRecordType: z11.string().nullable(),
  sourceRecordId: z11.string().uuid().nullable(),
  createdAt: z11.string().datetime(),
  readAt: z11.string().datetime().nullable()
});
var NotificationPageSchema = z11.object({
  items: z11.array(NotificationSchema),
  // Opaque keyset cursor (`createdAt|id`). Pass it back as `before`
  // to fetch the next page. Null when there are no more rows.
  nextCursor: z11.string().nullable()
});
var NotificationCountSchema = z11.object({
  unread: z11.number().int().nonnegative()
});
var NotificationEventPreferenceSchema = z11.object({
  event: NotificationEventTypeSchema,
  inapp: z11.boolean(),
  inappOverridden: z11.boolean()
});
var NotificationSettingsSchema = z11.object({
  emailSummary: z11.boolean(),
  emailEscalations: z11.boolean(),
  summaryTimeLocal: z11.string().regex(/^\d{2}:\d{2}(:\d{2})?$/),
  timezone: z11.string(),
  events: z11.array(NotificationEventPreferenceSchema)
});
var UpdateNotificationSettingsSchema = z11.object({
  emailSummary: z11.boolean(),
  emailEscalations: z11.boolean(),
  summaryTimeLocal: z11.string().regex(/^\d{2}:\d{2}(:\d{2})?$/),
  timezone: z11.string().min(1).max(64),
  events: z11.array(
    z11.object({
      eventKey: z11.string().max(120),
      inapp: z11.boolean().nullable()
    })
  )
}).partial();
var ListNotificationsQuerySchema = z11.object({
  status: z11.enum(["unread", "all"]).default("all"),
  limit: z11.coerce.number().int().positive().max(100).default(20),
  // Opaque cursor from a previous page's `nextCursor`. A malformed
  // value is ignored (treated as first page), not rejected.
  before: z11.string().optional()
});

// src/dto/outreach.dto.ts
import { z as z12 } from "zod";
var OutreachStageSchema = z12.enum([
  "new",
  "ready_to_reach_out",
  "to_follow_up",
  "interested",
  "demo_scheduled",
  "negotiating",
  "won",
  "lost"
]);
var OUTREACH_STAGE_LABELS = {
  new: "New",
  ready_to_reach_out: "Ready to reach out",
  to_follow_up: "To follow up",
  interested: "Interested",
  demo_scheduled: "Demo scheduled",
  negotiating: "Negotiating",
  won: "Won",
  lost: "Lost"
};
var OUTREACH_TERMINAL_STAGES = ["won", "lost"];
var OutreachNoteKindSchema = z12.enum(["note", "call", "email", "linkedin", "meeting"]);
var OptionalText = (max = 200) => z12.string().trim().min(1).max(max).nullable().optional();
var OutreachProspectSchema = z12.object({
  id: z12.string().uuid(),
  companyName: z12.string().min(1),
  websiteUrl: z12.string().nullable(),
  linkedinUrl: z12.string().nullable(),
  contactName: z12.string().nullable(),
  contactTitle: z12.string().nullable(),
  email: z12.string().nullable(),
  phone: z12.string().nullable(),
  city: z12.string().nullable(),
  state: z12.string().nullable(),
  source: z12.string().nullable(),
  stage: OutreachStageSchema,
  ownerAdminId: z12.string().uuid().nullable(),
  nextFollowUpAt: z12.string().datetime().nullable(),
  demoAt: z12.string().datetime().nullable(),
  tags: z12.array(z12.string()),
  createdAt: z12.string().datetime(),
  updatedAt: z12.string().datetime()
});
var CreateOutreachProspectSchema = z12.object({
  companyName: z12.string().trim().min(1).max(200),
  websiteUrl: OptionalText(),
  linkedinUrl: OptionalText(),
  contactName: OptionalText(),
  contactTitle: OptionalText(),
  email: OptionalEmailSchema,
  phone: OptionalText(40),
  city: OptionalText(120),
  state: OptionalText(40),
  source: OptionalText(120),
  stage: OutreachStageSchema.default("new"),
  ownerAdminId: z12.string().uuid().nullable().optional(),
  nextFollowUpAt: z12.string().datetime().nullable().optional(),
  demoAt: z12.string().datetime().nullable().optional(),
  tags: z12.array(z12.string().trim().min(1).max(40)).max(20).default([])
});
var UpdateOutreachProspectSchema = CreateOutreachProspectSchema.partial();
var OutreachNoteSchema = z12.object({
  id: z12.string().uuid(),
  prospectId: z12.string().uuid(),
  authorAdminId: z12.string().uuid().nullable(),
  kind: OutreachNoteKindSchema,
  body: z12.string(),
  createdAt: z12.string().datetime()
});
var CreateOutreachNoteSchema = z12.object({
  kind: OutreachNoteKindSchema.default("note"),
  body: z12.string().trim().min(1).max(5e3)
});
var ImportOutreachProspectsSchema = z12.object({
  rows: z12.array(CreateOutreachProspectSchema).min(1).max(500)
});
var ImportOutreachResultSchema = z12.object({
  created: z12.number().int().min(0)
});
var OutreachListQuerySchema = z12.object({
  search: z12.string().trim().max(200).optional(),
  stage: OutreachStageSchema.optional(),
  owner: z12.string().uuid().optional(),
  // Only the literal "true" enables the filter. `z.coerce.boolean()` would
  // wrongly treat "false"/"0" as true (every non-empty string is truthy).
  due: z12.string().optional().transform((v) => v === "true"),
  page: z12.coerce.number().int().min(1).default(1),
  limit: z12.coerce.number().int().min(1).max(200).default(50)
});
var OutreachProspectPageSchema = z12.object({
  items: z12.array(OutreachProspectSchema),
  total: z12.number().int().min(0),
  page: z12.number().int().min(1),
  limit: z12.number().int().min(1)
});
var OutreachSummarySchema = z12.object({
  total: z12.number().int().min(0),
  dueCount: z12.number().int().min(0),
  byStage: z12.record(OutreachStageSchema, z12.number().int().min(0))
});

// src/dto/tasks.dto.ts
import { z as z13 } from "zod";
var TaskStatusSchema = z13.enum([
  "open",
  "in_progress",
  "done",
  "cancelled",
  "snoozed"
]);
var TaskTypeSchema = z13.enum([
  "callback",
  "reply",
  "appointment_confirm",
  "no_show_recover",
  "manager_review",
  "crm_fix",
  "ai_draft_review",
  // A scheduled AI reply. Created when an inbound message would get a
  // delayed AI response (see the assignment-keyed reply delays); the
  // AI sweeps these by `todoAt` and answers when one comes due. It is
  // a real, visible task so the dealership can see the AI's pending
  // work — and a human reply completes it before the AI ever fires.
  "conversation.respond",
  // Transparent, task-driven AI behavior: every AI intent with timing
  // becomes a visible, cancellable row instead of an opaque agent loop.
  //  - follow_up: a richer follow-up the AI planned (schedule_follow_up
  //    / defer_reply) — sent customer-facing when it comes due.
  //  - recheck: scheduled on escalation at now + silence period; when it
  //    fires, the AI re-engages the customer or re-escalates if the
  //    hand-off was never picked up — making the silence window active.
  //  - handoff_check: verifies a teammate actually picked up a hand-off;
  //    if not, the AI nudges or takes the conversation back.
  "conversation.follow_up",
  "conversation.recheck",
  "conversation.handoff_check",
  // The AI spotted an opportunity a human should close (a buying
  // signal, a firm appointment ask, an explicit "talk to someone").
  // Unlike `escalate`, the AI keeps assisting — this is a positive,
  // collaborative hand-off, not "the AI is stuck." A human-actioned
  // task: it is NOT swept by the AiTaskTriggerCron; it lives as an
  // assigned task + a conversation flag (`needs_human_at`) + a
  // notification, cleared when a human takes over.
  "conversation.needs_human",
  // Reminder to send before a booked appointment (Draft 5 task model).
  // Cadence engine seeds these from service-type reminderTemplate;
  // also creatable manually for ad-hoc appointment prep.
  "appointment_reminder",
  // Long-term re-engagement check-in: 30 / 60 / 90-day drip touch for
  // leads that went cold without a sale. Distinct from conversation.follow_up
  // (short-term, conversation-scoped) — this is a multi-week cadence item.
  "re_engage",
  "other"
]);
var TaskReasonSchema = z13.enum([
  "ai_escalation",
  "callback_requested",
  "manual",
  "ai_appointment_request",
  "ai_no_response_followup",
  "ai_lost_lead_recovery",
  "other"
]);
var TaskViewSchema = z13.enum([
  "assigned_to_me",
  "ai_needs_help",
  // Leads the AI flagged for a human to close (`flag_for_human`)
  // — the open `conversation.needs_human` tasks.
  "needs_human",
  "unassigned",
  // All assigned, active work across the rooftop — the manager's
  // cross-team view, narrowable with the `assigneeUserId` filter.
  "team",
  // Whole-rooftop active tasks, assigned or not — the store-wide view.
  "store",
  "all",
  "overdue",
  "due_today"
]);
var TaskSchema = z13.object({
  id: z13.string().uuid(),
  dealershipId: z13.string().uuid(),
  rooftopId: z13.string().uuid().nullable(),
  contactId: z13.string().uuid().nullable(),
  leadId: z13.string().uuid().nullable(),
  title: z13.string(),
  description: z13.string().nullable(),
  status: TaskStatusSchema,
  taskType: TaskTypeSchema,
  reason: TaskReasonSchema.nullable(),
  createdByUserId: z13.string().uuid().nullable(),
  assignedUserId: z13.string().uuid().nullable(),
  todoAt: z13.string().datetime().nullable(),
  dueAt: z13.string().datetime().nullable(),
  availableAt: z13.string().datetime().nullable(),
  completedAt: z13.string().datetime().nullable(),
  completedByUserId: z13.string().uuid().nullable(),
  createdAt: z13.string().datetime(),
  updatedAt: z13.string().datetime()
});
var TaskPageSchema = z13.object({
  items: z13.array(TaskSchema),
  total: z13.number().int().nonnegative(),
  page: z13.number().int().positive(),
  pageSize: z13.number().int().positive()
});
var CreateTaskSchema = z13.object({
  dealershipId: z13.string().uuid(),
  rooftopId: z13.string().uuid().nullable(),
  contactId: z13.string().uuid().nullable(),
  leadId: z13.string().uuid().nullable().optional(),
  title: z13.string().trim().min(1).max(200),
  description: z13.string().trim().max(8e3).nullable().optional(),
  taskType: TaskTypeSchema.default("other"),
  reason: TaskReasonSchema.nullable().optional(),
  assignedUserId: z13.string().uuid().nullable().optional(),
  todoAt: z13.string().datetime().nullable().optional(),
  dueAt: z13.string().datetime().nullable().optional(),
  availableAt: z13.string().datetime().nullable().optional()
});
var UpdateTaskSchema = z13.object({
  title: z13.string().trim().min(1).max(200),
  description: z13.string().trim().max(8e3).nullable(),
  taskType: TaskTypeSchema,
  reason: TaskReasonSchema.nullable(),
  todoAt: z13.string().datetime().nullable(),
  dueAt: z13.string().datetime().nullable(),
  availableAt: z13.string().datetime().nullable()
}).partial();
var SnoozeTaskSchema = z13.object({
  availableAt: z13.string().datetime()
});
var AssignTaskSchema = z13.object({
  assignedUserId: z13.string().uuid().nullable()
});
var MoveTaskSchema = z13.object({
  status: z13.enum(["open", "in_progress", "done", "cancelled"])
});
var ListTasksQuerySchema = z13.object({
  view: TaskViewSchema.optional(),
  status: TaskStatusSchema.optional(),
  contactId: z13.string().uuid().optional(),
  leadId: z13.string().uuid().optional(),
  assigneeUserId: z13.string().uuid().optional(),
  search: z13.string().trim().max(200).optional(),
  dueFrom: z13.string().datetime().optional(),
  dueTo: z13.string().datetime().optional(),
  availableOnly: z13.coerce.boolean().optional(),
  // When false (default), AI-created tasks are excluded from the list.
  // Set to true to include them (the "Show Mika's tasks" toggle).
  includeAi: z13.coerce.boolean().optional(),
  page: z13.coerce.number().int().min(1).optional(),
  pageSize: z13.coerce.number().int().min(1).max(100).optional()
}).partial();
var TaskCountsSchema = z13.object({
  my: z13.number().int().nonnegative(),
  team: z13.number().int().nonnegative(),
  store: z13.number().int().nonnegative(),
  unassigned: z13.number().int().nonnegative(),
  mikaFollowups: z13.number().int().nonnegative(),
  mikaReminders: z13.number().int().nonnegative(),
  mikaReengage: z13.number().int().nonnegative()
});

// src/dto/health.dto.ts
import { z as z14 } from "zod";
var HealthStatusSchema = z14.object({
  status: z14.enum(["ok", "degraded", "down"]),
  uptimeSeconds: z14.number().nonnegative(),
  version: z14.string(),
  checks: z14.record(z14.string(), z14.enum(["ok", "down"])).optional()
});

// src/dto/inventory-import.dto.ts
import { z as z16 } from "zod";

// src/dto/inventory-rule.dto.ts
import { z as z15 } from "zod";
var InventoryRuleFieldSchema = z15.enum([
  "rooftop",
  "condition",
  "make",
  "fuel"
]);
var InventoryRuleOperatorSchema = z15.enum([
  "equals",
  "not_equals",
  "contains",
  "not_contains",
  "in",
  "not_in"
]);
var InventoryRuleCombinatorSchema = z15.enum(["all", "any"]);
var InventoryRuleSchema = z15.object({
  field: InventoryRuleFieldSchema,
  operator: InventoryRuleOperatorSchema,
  // `string` for equals/contains/not_equals/not_contains.
  // `string[]` (≤ 50 values) for in/not_in.
  value: z15.union([
    z15.string().trim().min(1).max(120),
    z15.array(z15.string().trim().min(1).max(120)).min(1).max(50)
  ])
});
var InventoryRuleGroupSchema = z15.lazy(
  () => z15.object({
    combinator: InventoryRuleCombinatorSchema,
    rules: z15.array(z15.union([InventoryRuleSchema, InventoryRuleGroupSchema])).max(50)
  })
);

// src/dto/inventory-import.dto.ts
var InventoryImportTypeSchema = z16.enum(["sftp", "scraping", "api"]);
var InventoryImportProviderSchema = z16.enum([
  "vauto",
  "spaceauto",
  "homenet",
  "website",
  "dealeron",
  "dealercom",
  "dealercloud",
  "teamvelocity",
  "overfuel",
  "dealerinspire",
  "lumika-json"
]);
var InventoryImportSchema = z16.object({
  id: z16.string().uuid(),
  dealershipId: z16.string().uuid(),
  rooftopId: z16.string().uuid(),
  type: InventoryImportTypeSchema,
  provider: InventoryImportProviderSchema,
  // `null` rule means "match every parsed row" (consumer short-circuit).
  rule: InventoryRuleGroupSchema.nullable(),
  path: z16.string().nullable(),
  label: z16.string(),
  /**
   * Read-DTO masked summary of the encrypted-at-rest provider
   * credential. Cleartext never leaves the backend; this carries
   * `••••<last4>` for `api` imports and `null` for `sftp`/`scraping`.
   */
  tokenMasked: z16.string().nullable(),
  /**
   * Pinned Cars Commerce listing id for DealerInspire scrapes whose
   * auto-discovery is Cloudflare-challenged. Numeric string; null for
   * every other provider. Not a secret, so it ships in clear (unlike
   * `tokenMasked`).
   */
  ccid: z16.string().nullable(),
  lastPulledAt: z16.string().datetime().nullable(),
  lastPulledError: z16.string().nullable(),
  createdAt: z16.string().datetime(),
  updatedAt: z16.string().datetime()
});
var CreateInventoryImportSchema = z16.object({
  rooftopId: z16.string().uuid(),
  type: InventoryImportTypeSchema,
  provider: InventoryImportProviderSchema,
  rule: InventoryRuleGroupSchema.nullable().default(null),
  // Operator-typed S3 key; the controller normalises leading/trailing
  // slashes before persistence.
  path: z16.string().trim().max(512).nullable().default(null),
  label: z16.string().trim().min(1).max(120),
  /**
   * Provider credential (API key). Required for `type='api'`
   * imports — used by the hourly pull cron to authenticate
   * against the provider's API. Encrypted at rest server-side.
   */
  token: z16.string().trim().min(1).max(512).nullable().default(null),
  /**
   * Pinned Cars Commerce listing id — only meaningful for a
   * `dealerinspire` scraping import whose discovery is challenged.
   * Numeric; the server stores it only for that provider.
   */
  ccid: z16.string().trim().regex(/^\d{4,9}$/, "Cars Commerce listing id must be numeric.").nullable().default(null)
}).refine(
  (v) => v.type !== "sftp" || (v.path?.trim().length ?? 0) > 0,
  {
    message: "Path is required for SFTP imports.",
    path: ["path"]
  }
).refine((v) => v.type !== "api" || (v.token?.trim().length ?? 0) > 0, {
  message: "Token is required for API imports.",
  path: ["token"]
}).refine(
  (v) => v.type !== "scraping" || !v.path?.trim() || /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(v.path.trim()),
  {
    message: "Use a bare domain, e.g. example.com.",
    path: ["path"]
  }
);
var _UpdateBodySchema = z16.object({
  rooftopId: z16.string().uuid().optional(),
  type: InventoryImportTypeSchema.optional(),
  provider: InventoryImportProviderSchema.optional(),
  rule: InventoryRuleGroupSchema.nullable().optional(),
  path: z16.string().trim().max(512).nullable().optional(),
  label: z16.string().trim().min(1).max(120).optional(),
  /** Token rotation — re-encrypted server-side on every set. */
  token: z16.string().trim().min(1).max(512).nullable().optional(),
  /** Pinned Cars Commerce listing id (DealerInspire only); null clears it. */
  ccid: z16.string().trim().regex(/^\d{4,9}$/, "Cars Commerce listing id must be numeric.").nullable().optional()
});
var UpdateInventoryImportSchema = _UpdateBodySchema.refine(
  (v) => (
    // If the body sets `type='sftp'`, it must also carry a non-empty path.
    v.type !== "sftp" || v.path !== void 0 && (v.path?.trim().length ?? 0) > 0
  ),
  {
    message: "Path is required for SFTP imports.",
    path: ["path"]
  }
).refine(
  (v) => v.type !== "scraping" || !v.path?.trim() || /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(v.path.trim()),
  {
    message: "Use a bare domain, e.g. example.com.",
    path: ["path"]
  }
);
var InventoryImportListSchema = z16.array(InventoryImportSchema);

// src/dto/inventory-search.dto.ts
import { z as z17 } from "zod";
var InventoryRangeSchema = z17.object({
  from: z17.number().optional(),
  to: z17.number().optional()
});
var InventoryStatusSchema = z17.enum([
  "available",
  "intransit",
  "pending",
  "sold"
]);
var INVENTORY_STATUS_LABELS = {
  available: "Available",
  intransit: "In Transit",
  pending: "Pending",
  sold: "Sold"
};
var InventorySearchFiltersSchema = z17.object({
  vins: z17.array(z17.string()).optional(),
  make: z17.array(z17.string()).optional(),
  model: z17.array(z17.string()).optional(),
  trim: z17.array(z17.string()).optional(),
  body: z17.array(z17.string()).optional(),
  color: z17.array(z17.string()).optional(),
  interiorColor: z17.array(z17.string()).optional(),
  fuel: z17.array(z17.string()).optional(),
  transmission: z17.array(z17.string()).optional(),
  driveTrain: z17.array(z17.string()).optional(),
  condition: z17.array(z17.string()).optional(),
  status: z17.array(InventoryStatusSchema).optional(),
  rooftopId: z17.array(z17.string().uuid()).optional(),
  importId: z17.array(z17.string().uuid()).optional(),
  year: InventoryRangeSchema.optional(),
  mileage: InventoryRangeSchema.optional(),
  price: InventoryRangeSchema.optional()
});
var InventorySortFieldSchema = z17.enum([
  "price",
  "mileage",
  "year",
  "make",
  "model",
  "stock",
  "stockDate",
  "soldDate",
  "createdAt"
]);
var InventorySearchRequestSchema = z17.object({
  query: z17.string().trim().max(120).optional(),
  filters: InventorySearchFiltersSchema.default({}),
  sort: z17.object({
    field: InventorySortFieldSchema,
    order: z17.enum(["asc", "desc"]).default("asc")
  }).optional(),
  pagination: z17.object({
    skip: z17.number().int().min(0).max(1e5).default(0),
    limit: z17.number().int().min(1).max(200).default(24)
  }).default({ skip: 0, limit: 24 }),
  facets: z17.boolean().default(true)
});
var TermFacetSchema = z17.array(
  z17.object({ value: z17.string(), count: z17.number().int() })
);
var RooftopFacetSchema = z17.array(
  z17.object({
    value: z17.string().uuid(),
    label: z17.string(),
    count: z17.number().int()
  })
);
var RangeFacetSchema = z17.object({
  from: z17.number().nullable(),
  to: z17.number().nullable()
});
var InventoryFacetsSchema = z17.object({
  make: TermFacetSchema,
  model: TermFacetSchema,
  trim: TermFacetSchema,
  body: TermFacetSchema,
  color: TermFacetSchema,
  interiorColor: TermFacetSchema,
  fuel: TermFacetSchema,
  transmission: TermFacetSchema,
  driveTrain: TermFacetSchema,
  condition: TermFacetSchema,
  status: TermFacetSchema,
  rooftop: RooftopFacetSchema,
  year: RangeFacetSchema,
  mileage: RangeFacetSchema,
  price: RangeFacetSchema
});
var InventorySearchResponseSchema = z17.object({
  total: z17.number().int(),
  vehicles: z17.array(InventoryVehicleSchema),
  facets: InventoryFacetsSchema.nullable()
});
var InventorySuggestionSchema = z17.object({
  // What kind of thing the suggestion points at, so the UI can route a
  // click to the right filter (or straight to a VDP for a VIN/stock).
  type: z17.enum(["vin", "stock", "make", "model", "trim", "make_model"]),
  value: z17.string(),
  label: z17.string(),
  count: z17.number().int().optional()
});
var InventorySuggestRequestSchema = z17.object({
  query: z17.string().trim().min(1).max(80),
  limit: z17.number().int().min(1).max(20).default(10)
});
var InventorySuggestResponseSchema = z17.array(InventorySuggestionSchema);

// src/dto/sftp.dto.ts
import { z as z18 } from "zod";
var SftpAccountStatusSchema = z18.enum(["active", "disabled", "rotating"]);
var SftpUsernameSchema = z18.string().trim().min(3).max(32).regex(/^[a-z0-9_-]+$/, "Username may contain only lowercase letters, digits, '-', '_'.");
var SftpFolderSchema = z18.string().trim().min(1).max(128).regex(
  /^[a-z0-9_/-]+$/,
  "Folder may contain only lowercase letters, digits, '-', '_', '/'."
).refine((v) => !v.startsWith("/") && !v.endsWith("/"), {
  message: "Folder must not start or end with '/'."
}).refine((v) => !v.includes("//"), {
  message: "Folder must not contain '//'."
});
var SftpPasswordSchema = z18.string().min(16, "Password must be at least 16 characters.").max(128).regex(
  /^[\x20-\x7E]+$/u,
  "Password must contain only printable ASCII characters."
);
var SftpAccountRecordSchema = z18.object({
  id: z18.string().uuid(),
  username: SftpUsernameSchema,
  folder: z18.string(),
  status: SftpAccountStatusSchema,
  notes: z18.string().nullable(),
  lastSeenAt: z18.string().datetime().nullable(),
  createdByAdminId: z18.string().uuid().nullable(),
  createdAt: z18.string().datetime(),
  updatedAt: z18.string().datetime()
});
var CreateSftpAccountSchema = z18.object({
  // Required — the integrator-facing handle. Unique.
  username: SftpUsernameSchema,
  // Reserved folder root on the bucket. When omitted, the service
  // falls back to the username (so the credential's home is
  // discoverable without typing).
  folder: SftpFolderSchema.optional(),
  // Optional cleartext password. When omitted, the service
  // auto-generates a strong base64url value. The cleartext is
  // returned exactly once in the create response — no echo back later.
  password: SftpPasswordSchema.optional(),
  notes: z18.string().max(2e3).nullable().optional()
});
var UpdateSftpAccountSchema = z18.object({
  status: SftpAccountStatusSchema.optional(),
  notes: z18.string().max(2e3).nullable().optional()
});
var RotateSftpAccountSchema = z18.object({
  password: SftpPasswordSchema.optional()
});
var SftpAccountWithSecretSchema = SftpAccountRecordSchema.extend({
  password: z18.string().min(16),
  host: z18.string(),
  port: z18.number().int().positive()
});
var SftpFileEntrySchema = z18.object({
  name: z18.string(),
  path: z18.string(),
  type: z18.enum(["file", "directory"]),
  size: z18.number().int().nullable(),
  lastModified: z18.string().datetime().nullable()
});
var SftpListResponseSchema = z18.object({
  currentPath: z18.string(),
  entries: z18.array(SftpFileEntrySchema)
});
var SftpUploadUrlRequestSchema = z18.object({
  path: z18.string().min(1).max(1024),
  contentType: z18.string().max(127).regex(/^[\w.+-]+\/[\w.+-]+$/, "Use a valid MIME type.").optional()
});
var SftpUploadUrlResponseSchema = z18.object({
  url: z18.string().url(),
  path: z18.string(),
  headers: z18.record(z18.string(), z18.string())
});
var SftpDownloadUrlResponseSchema = z18.object({
  url: z18.string().url(),
  path: z18.string()
});
var SftpDeletePathRequestSchema = z18.object({
  path: z18.string().min(1).max(1024)
});
var SftpCreateFolderRequestSchema = z18.object({
  path: z18.string().min(1).max(128)
});

// src/email/invitation-email.ts
var MEMBERSHIP_ROLE_LABELS = {
  dealership_owner: "Owner",
  dealership_admin: "Admin",
  sales_manager: "Sales Manager",
  service_manager: "Service Manager",
  bdc_agent: "BDC Agent",
  sales_agent: "Sales Agent",
  service_agent: "Service Agent",
  readonly: "Viewer"
};
var COLOR = {
  pageBg: "#eef0f2",
  card: "#ffffff",
  border: "#e6e8eb",
  ink: "#0f0e0d",
  muted: "#8a9097",
  body: "#3f4448",
  brand: "#5c7a9c",
  panel: "#f5f7f9"
};
var FONT_STACK = "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif";
function escapeHtml(value) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
function trimTrailingSlash(value) {
  return value.replace(/\/+$/, "");
}
function renderInvitationEmail(input) {
  const appUrl = trimTrailingSlash(input.appUrl);
  const logoUrl = input.logoUrl ?? `${appUrl}/lumika-email.png`;
  const roleLabel = MEMBERSHIP_ROLE_LABELS[input.role];
  const dealership = input.dealershipName.trim();
  const greetingName = input.recipientName?.trim();
  const subject = `Welcome to Lumika \u2014 you've been added to ${dealership}`;
  const text = [
    `Welcome to Lumika!`,
    ``,
    greetingName ? `Hi ${greetingName},` : `Hi there,`,
    ``,
    `You've been added as ${roleLabel} to manage ${dealership} on Lumika.`,
    ``,
    `Lumika is your AI-powered command center for every customer conversation. Log in to get started:`,
    appUrl,
    ``,
    `You're receiving this email because you were added to a team on Lumika.`
  ].join("\n");
  const safeDealership = escapeHtml(dealership);
  const safeRole = escapeHtml(roleLabel);
  const safeAppUrl = escapeHtml(appUrl);
  const safeLogoUrl = escapeHtml(logoUrl);
  const greeting = greetingName ? `Hi ${escapeHtml(greetingName)},` : "Hi there,";
  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<meta name="color-scheme" content="light">
<meta name="supported-color-schemes" content="light">
<title>${escapeHtml(subject)}</title>
</head>
<body style="margin:0;padding:0;background-color:${COLOR.pageBg};">
<div style="display:none;max-height:0;overflow:hidden;opacity:0;">You've been added as ${safeRole} to manage ${safeDealership} on Lumika.</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${COLOR.pageBg};">
<tr>
<td align="center" style="padding:32px 16px;">
<table role="presentation" width="480" cellpadding="0" cellspacing="0" border="0" style="width:480px;max-width:100%;background-color:${COLOR.card};border:1px solid ${COLOR.border};border-radius:16px;overflow:hidden;">
<!-- Header -->
<tr>
<td style="padding:32px 32px 0 32px;">
<table role="presentation" cellpadding="0" cellspacing="0" border="0">
<tr>
<td style="vertical-align:middle;padding-right:10px;">
<img src="${safeLogoUrl}" width="34" height="34" alt="Lumika" style="display:block;width:34px;height:34px;border:0;">
</td>
<td style="vertical-align:middle;">
<span style="font-family:${FONT_STACK};font-size:21px;font-weight:600;letter-spacing:0.2px;color:${COLOR.ink};">lumika</span>
</td>
</tr>
</table>
</td>
</tr>
<!-- Body -->
<tr>
<td style="padding:28px 32px 4px 32px;font-family:${FONT_STACK};">
<div style="font-size:12px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:${COLOR.brand};">You're invited</div>
<h1 style="margin:8px 0 0 0;font-size:26px;line-height:1.25;font-weight:700;color:${COLOR.ink};">Welcome to Lumika</h1>
<p style="margin:18px 0 0 0;font-size:15px;line-height:1.6;color:${COLOR.body};">${greeting}</p>
<p style="margin:12px 0 0 0;font-size:15px;line-height:1.6;color:${COLOR.body};">You've been added as <strong style="color:${COLOR.ink};">${safeRole}</strong> to manage <strong style="color:${COLOR.ink};">${safeDealership}</strong> on Lumika.</p>
</td>
</tr>
<!-- Detail panel -->
<tr>
<td style="padding:20px 32px 0 32px;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${COLOR.panel};border-radius:12px;">
<tr>
<td style="padding:16px 18px;font-family:${FONT_STACK};">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
<tr>
<td style="font-size:12px;color:${COLOR.muted};padding-bottom:2px;">Dealership</td>
</tr>
<tr>
<td style="font-size:15px;font-weight:600;color:${COLOR.ink};padding-bottom:12px;">${safeDealership}</td>
</tr>
<tr>
<td style="font-size:12px;color:${COLOR.muted};padding-bottom:2px;">Your role</td>
</tr>
<tr>
<td style="font-size:15px;font-weight:600;color:${COLOR.ink};">${safeRole}</td>
</tr>
</table>
</td>
</tr>
</table>
</td>
</tr>
<!-- CTA -->
<tr>
<td style="padding:26px 32px 4px 32px;font-family:${FONT_STACK};">
<p style="margin:0 0 18px 0;font-size:15px;line-height:1.6;color:${COLOR.body};">Lumika is your AI-powered command center for every customer conversation. Sign in to get started.</p>
<table role="presentation" cellpadding="0" cellspacing="0" border="0">
<tr>
<td align="center" bgcolor="${COLOR.brand}" style="border-radius:10px;">
<a href="${safeAppUrl}" target="_blank" style="display:inline-block;padding:14px 30px;font-family:${FONT_STACK};font-size:15px;font-weight:600;color:#ffffff;text-decoration:none;border-radius:10px;">Log in to Lumika</a>
</td>
</tr>
</table>
<p style="margin:18px 0 0 0;font-size:13px;line-height:1.6;color:${COLOR.muted};">Or paste this link into your browser:<br><a href="${safeAppUrl}" target="_blank" style="color:${COLOR.brand};text-decoration:none;">${safeAppUrl}</a></p>
</td>
</tr>
<!-- Footer -->
<tr>
<td style="padding:28px 32px 32px 32px;font-family:${FONT_STACK};">
<div style="border-top:1px solid ${COLOR.border};padding-top:18px;font-size:12px;line-height:1.6;color:${COLOR.muted};">You're receiving this email because you were added to a team on Lumika.</div>
</td>
</tr>
</table>
</td>
</tr>
</table>
</body>
</html>`;
  return { subject, text, html };
}

// src/email/email-code-email.ts
var COLOR2 = {
  pageBg: "#eef0f2",
  card: "#ffffff",
  border: "#e6e8eb",
  ink: "#0f0e0d",
  muted: "#8a9097",
  body: "#3f4448",
  brand: "#5c7a9c",
  panel: "#f5f7f9"
};
var FONT_STACK2 = "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif";
function escapeHtml2(value) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
function renderEmailCodeEmail(input) {
  const logoUrl = input.logoUrl ?? "https://app.lumika.ai/lumika-email.png";
  const safeLogoUrl = escapeHtml2(logoUrl);
  const safeCode = escapeHtml2(input.code);
  const subject = "Your Lumika sign-in code";
  const text = [
    `Your Lumika sign-in code`,
    ``,
    `Code: ${input.code}`,
    ``,
    `This code expires in ${input.expiryMinutes} minute${input.expiryMinutes === 1 ? "" : "s"}.`,
    ``,
    `If you didn't request this code, you can safely ignore this email.`
  ].join("\n");
  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<meta name="color-scheme" content="light">
<meta name="supported-color-schemes" content="light">
<title>${escapeHtml2(subject)}</title>
</head>
<body style="margin:0;padding:0;background-color:${COLOR2.pageBg};">
<div style="display:none;max-height:0;overflow:hidden;opacity:0;">Your Lumika sign-in code: ${safeCode}</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${COLOR2.pageBg};">
<tr>
<td align="center" style="padding:32px 16px;">
<table role="presentation" width="480" cellpadding="0" cellspacing="0" border="0" style="width:480px;max-width:100%;background-color:${COLOR2.card};border:1px solid ${COLOR2.border};border-radius:16px;overflow:hidden;">
<!-- Header -->
<tr>
<td style="padding:32px 32px 0 32px;">
<table role="presentation" cellpadding="0" cellspacing="0" border="0">
<tr>
<td style="vertical-align:middle;padding-right:10px;">
<img src="${safeLogoUrl}" width="34" height="34" alt="Lumika" style="display:block;width:34px;height:34px;border:0;">
</td>
<td style="vertical-align:middle;">
<span style="font-family:${FONT_STACK2};font-size:21px;font-weight:600;letter-spacing:0.2px;color:${COLOR2.ink};">lumika</span>
</td>
</tr>
</table>
</td>
</tr>
<!-- Body -->
<tr>
<td style="padding:28px 32px 4px 32px;font-family:${FONT_STACK2};">
<div style="font-size:12px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:${COLOR2.brand};">Sign in</div>
<h1 style="margin:8px 0 0 0;font-size:26px;line-height:1.25;font-weight:700;color:${COLOR2.ink};">Your sign-in code</h1>
<p style="margin:18px 0 0 0;font-size:15px;line-height:1.6;color:${COLOR2.body};">Use the code below to sign in to Lumika. It expires in ${input.expiryMinutes} minute${input.expiryMinutes === 1 ? "" : "s"}.</p>
</td>
</tr>
<!-- Code panel -->
<tr>
<td style="padding:20px 32px 0 32px;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${COLOR2.panel};border-radius:12px;">
<tr>
<td align="center" style="padding:24px 18px;font-family:${FONT_STACK2};">
<div style="font-size:36px;font-weight:700;letter-spacing:8px;color:${COLOR2.ink};font-variant-numeric:tabular-nums;">${safeCode}</div>
</td>
</tr>
</table>
</td>
</tr>
<!-- Footer -->
<tr>
<td style="padding:28px 32px 32px 32px;font-family:${FONT_STACK2};">
<div style="border-top:1px solid ${COLOR2.border};padding-top:18px;font-size:12px;line-height:1.6;color:${COLOR2.muted};">If you didn't request this code, you can safely ignore this email.</div>
</td>
</tr>
</table>
</td>
</tr>
</table>
</body>
</html>`;
  return { subject, text, html };
}

// src/mentions.ts
var MENTION_REGEX = /@\[([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\]/g;
function formatMention(userId) {
  return `@[${userId}]`;
}
function extractMentionIds(text) {
  if (!text) return [];
  const ids = [];
  const seen = /* @__PURE__ */ new Set();
  for (const match of text.matchAll(MENTION_REGEX)) {
    const id = match[1];
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}
function splitMentions(text) {
  if (!text) return [];
  const segments = [];
  let cursor = 0;
  for (const match of text.matchAll(MENTION_REGEX)) {
    const id = match[1];
    const token = match[0];
    if (!id || !token) continue;
    const start = match.index ?? 0;
    if (start > cursor) {
      segments.push({ type: "text", value: text.slice(cursor, start) });
    }
    segments.push({ type: "mention", id });
    cursor = start + token.length;
  }
  if (cursor < text.length) {
    segments.push({ type: "text", value: text.slice(cursor) });
  }
  return segments;
}
function replaceMentions(text, resolve) {
  return text.replace(MENTION_REGEX, (_token, id) => resolve(id));
}
function truncateMentions(text, max) {
  const collapsed = (text ?? "").replace(/\s+/g, " ").trim();
  if (collapsed.length <= max) return collapsed;
  let cut = collapsed.slice(0, max - 1);
  if (cut.lastIndexOf("@[") > cut.lastIndexOf("]")) {
    cut = cut.slice(0, cut.lastIndexOf("@[")).trimEnd();
  }
  return `${cut}\u2026`;
}

// src/inventory-providers.ts
var INVENTORY_PROVIDER_IDS = [
  "spaceauto",
  "vauto",
  "homenet",
  // Legacy recipe-based scraping rows; kept for existing data, hidden
  // from the create form. New scraping imports pick a platform below.
  "website",
  // Website-scraping platforms — each maps to a first-class scraper in
  // apps/backend/src/inventory-scrapers (no CSV mapper).
  "dealeron",
  "dealercom",
  "dealercloud",
  "teamvelocity",
  "overfuel",
  "dealerinspire",
  // SFTP-uploaded JSON file in the canonical ScrapedVehicle shape —
  // the upload path for inventory scraped outside the platform (e.g.
  // Cloudflare-hard sites scraped locally).
  "lumika-json"
];
var INVENTORY_PROVIDERS = [
  { id: "spaceauto", name: "Space Auto" },
  { id: "vauto", name: "vAuto" },
  { id: "homenet", name: "Homenet" },
  { id: "website", name: "Website Scraping (legacy)" },
  { id: "dealeron", name: "DealerOn" },
  { id: "dealercom", name: "Dealer.com" },
  { id: "dealercloud", name: "dealr.cloud" },
  { id: "teamvelocity", name: "Team Velocity" },
  { id: "overfuel", name: "Overfuel" },
  { id: "dealerinspire", name: "DealerInspire" },
  { id: "lumika-json", name: "Lumika JSON" }
];
var SCRAPING_PROVIDER_IDS = [
  "dealeron",
  "dealercom",
  "dealercloud",
  "teamvelocity",
  "overfuel",
  "dealerinspire"
];
function isInventoryProviderId(value) {
  return typeof value === "string" && INVENTORY_PROVIDER_IDS.includes(value);
}
function inventoryProviderName(id) {
  return INVENTORY_PROVIDERS.find((p) => p.id === id)?.name ?? id;
}
function isScrapingProviderId(value) {
  return typeof value === "string" && SCRAPING_PROVIDER_IDS.includes(value);
}
function normalizeScrapeDomain(input) {
  let v = input.trim().toLowerCase();
  v = v.replace(/^[a-z][a-z0-9+.-]*:\/\//, "");
  v = v.replace(/[/?#].*$/, "");
  v = v.replace(/:\d+$/, "");
  v = v.replace(/^www\./, "");
  return v;
}

// src/timezone.ts
function wallParts(instant, timeZone) {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  });
  const get = (type) => {
    const v = fmt.formatToParts(instant).find((p) => p.type === type)?.value ?? "0";
    return Number(v === "24" ? "0" : v);
  };
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
    second: get("second")
  };
}
var toDate = (instant) => instant instanceof Date ? instant : new Date(instant);
function formatInTimeZone(instant, timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).formatToParts(toDate(instant));
  const get = (type) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("weekday")}, ${get("month")} ${get("day")}, ${get("year")}, ${get("hour")}:${get("minute")} ${get("dayPeriod")}`;
}
function utcToWallTime(instant, timeZone) {
  const p = wallParts(toDate(instant), timeZone);
  const pad = (n) => String(n).padStart(2, "0");
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}
function wallTimeToUtc(wallTime, timeZone) {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/.exec(
    wallTime.trim()
  );
  if (!m) throw new Error(`wallTimeToUtc: invalid wall time "${wallTime}"`);
  const [, y, mo, d, h, mi, s] = m;
  const target = Date.UTC(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    Number(s ?? "0")
  );
  let guess = target;
  for (let i = 0; i < 2; i++) {
    const p = wallParts(new Date(guess), timeZone);
    const seen = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    guess += target - seen;
  }
  return new Date(guess);
}
function wallClockDate(instant, timeZone) {
  const p = wallParts(toDate(instant), timeZone);
  return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

// src/lib/activity-changes.ts
var LEAD_STATUS_LABELS = {
  new: "New",
  working: "Working",
  appointment: "Appointment",
  won: "Won",
  lost: "Lost"
};
function leadStatusLabel(status) {
  return LEAD_STATUS_LABELS[status] ?? status;
}
var CONTACT_STATUS_LABELS = {
  anonymous: "Anonymous",
  new_lead: "New lead",
  engaged: "Engaged",
  appointment_scheduled: "Appointment scheduled",
  appointment_attended: "Appointment attended",
  sold: "Sold",
  service_customer: "Service customer",
  inactive: "Inactive",
  lost: "Lost"
};
function contactStatusLabel(status) {
  return CONTACT_STATUS_LABELS[status] ?? status.replace(/_/g, " ");
}
var CHANGE_FIELD_LABELS = {
  assignedUserId: "assignee",
  snoozedUntil: "snooze",
  channelId: "channel",
  primaryChannel: "channel",
  closed: "state",
  status: "status",
  firstName: "first name",
  lastName: "last name",
  businessName: "business name",
  primaryEmail: "email",
  primaryPhone: "phone",
  city: "city",
  state: "state",
  zip: "zip",
  addressLine1: "address",
  preferredChannel: "preferred channel",
  preferredLanguage: "language",
  leadSource: "lead source",
  birthDate: "birthday",
  optoutEmail: "email opt-out",
  optoutSms: "SMS opt-out",
  optoutVoice: "voice opt-out",
  notes: "notes",
  tag: "tag",
  value: "value",
  isPrimary: "primary",
  type: "type"
};
function changeFieldLabel(field) {
  return CHANGE_FIELD_LABELS[field] ?? field.replace(/_/g, " ");
}
function parseActivityChanges(body) {
  if (!body) return null;
  const trimmed = body.trimStart();
  if (!trimmed.startsWith("{")) return null;
  try {
    const parsed = JSON.parse(trimmed);
    const changes = parsed.changes;
    if (!changes || typeof changes !== "object" || Array.isArray(changes)) return null;
    const out = {};
    for (const [field, value] of Object.entries(changes)) {
      if (value && typeof value === "object" && "before" in value && "after" in value) {
        out[field] = value;
      }
    }
    return Object.keys(out).length > 0 ? out : null;
  } catch {
    return null;
  }
}
var ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
var UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
var VALUE_MAX = 60;
function truncate(value) {
  return value.length > VALUE_MAX ? `${value.slice(0, VALUE_MAX - 1)}\u2026` : value;
}
function formatChangeValue(field, value, resolvers = {}) {
  if (value === null || value === void 0 || value === "") {
    return field === "assignedUserId" ? "Unassigned" : "\u2014";
  }
  if (field === "status") {
    const str2 = String(value);
    return LEAD_STATUS_LABELS[str2] ?? CONTACT_STATUS_LABELS[str2] ?? str2.replace(/_/g, " ");
  }
  if (field === "closed") return value ? "closed" : "open";
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (field === "assignedUserId") {
    return resolvers.userName?.(String(value)) ?? "a teammate";
  }
  if (field === "channelId" || field === "primaryChannel") {
    const resolved = field === "channelId" ? resolvers.channelName?.(String(value)) : null;
    if (resolved) return resolved;
    return UUID.test(String(value)) ? "another channel" : String(value);
  }
  const str = String(value);
  if (ISO_DATETIME.test(str)) {
    return resolvers.formatDateTime?.(str) ?? str.slice(0, 10);
  }
  if (UUID.test(str)) return "\u2014";
  return truncate(str);
}
var LABEL_ONLY_FIELDS = /* @__PURE__ */ new Set(["notes"]);
function summarizeActivityChanges(body, resolvers = {}) {
  const changes = parseActivityChanges(body);
  if (!changes) return null;
  const fields = Object.keys(changes);
  const visible = fields.slice(0, 3);
  const parts = visible.map((field) => {
    const label = changeFieldLabel(field);
    const titled = label.charAt(0).toUpperCase() + label.slice(1);
    if (LABEL_ONLY_FIELDS.has(field)) return `${titled} updated`;
    const { before, after } = changes[field];
    return `${titled}: ${formatChangeValue(field, before, resolvers)} \u2192 ${formatChangeValue(field, after, resolvers)}`;
  });
  const extra = fields.length - visible.length;
  return `${parts.join("; ")}${extra > 0 ? ` (+${extra} more)` : ""}`;
}

// src/lib/telephony-error-codes.ts
var TELEPHONY_ERROR_MAP = {
  // Trust Hub / A2P submission
  "30001": {
    message: "The EIN we sent doesn't match an active business with the IRS.",
    fix: "Double-check the EIN in Business profile. Format must be 12-3456789."
  },
  "30002": {
    message: "The website couldn't be reached.",
    fix: "Make sure the website is publicly reachable over HTTPS and includes a privacy policy."
  },
  "30003": {
    message: "Sample SMS messages don't comply with carrier guidelines.",
    fix: "Every sample needs the dealership name, opt-out (STOP), and avoid prohibited content."
  },
  "30004": {
    message: "The authorised representative email is on a free webmail domain.",
    fix: "Use an email on the dealership's domain (e.g. you@dealership.com)."
  },
  // Brand registration
  "23104": {
    message: "Brand registration was rejected by the carrier.",
    fix: "Open the brand on the A2P wizard; the rejection reasons are listed there."
  },
  // Campaign
  "23108": {
    message: "Campaign rejected \u2014 use case doesn't match the sample messages.",
    fix: "Pick a use case (e.g. Customer Care) that fits the sample messages, or resubmit with samples that match."
  },
  // SMS send-time failures
  "30007": {
    message: "Carrier filtered the message as spam.",
    fix: "Soften the marketing tone, remove excessive caps/punctuation, and ensure STOP instructions."
  },
  "30034": {
    message: "This number isn't registered for A2P SMS yet.",
    fix: "Finish the A2P wizard for this Telephony Account before sending SMS."
  }
};
function translateCarrierError(args) {
  const code = args.code != null ? String(args.code).trim() : "";
  const known = code ? TELEPHONY_ERROR_MAP[code] : void 0;
  if (known) return known;
  const raw = (args.rawMessage ?? "").trim();
  if (!raw) {
    return { message: "Something went wrong on the carrier side. Try again or contact support." };
  }
  const stripped = raw.replace(/^Error\s+\d+[:\s-]+/i, "");
  return { message: stripped };
}

// src/lib/telephony-pricing.ts
var TELEPHONY_PRICING = {
  /** Monthly carrier fee to keep a local 10-digit number. */
  phoneNumberLocalMonthly: 1.15,
  /** Monthly carrier fee to keep a toll-free number. */
  phoneNumberTollFreeMonthly: 2,
  /** One-time A2P brand registration fee. */
  a2pBrandRegistration: 4,
  /** One-time external vetting fee — mandatory for STANDARD brands. */
  a2pStandardVetting: 40,
  /** One-time A2P campaign registration / vetting fee. */
  a2pCampaignRegistration: 15,
  /** Monthly carrier fee for a standard-tier campaign. */
  a2pCampaignMonthlyStandard: 10,
  /** Monthly carrier fee for a low-volume campaign. */
  a2pCampaignMonthlyLowVolume: 2
};
function formatUsd(amount) {
  return `$${amount.toFixed(2)}`;
}

// src/lib/usage-taxonomy.ts
var USAGE_TYPES = ["ai", "twilio", "dms"];
var AI_USAGE_KINDS = [
  "text_generation",
  "transcription",
  "image_generation",
  "phone_call"
];
function isAiUsageKind(value) {
  return AI_USAGE_KINDS.includes(value);
}

// src/lib/contact-display.ts
function contactDisplayName(input) {
  if (input.businessName?.trim()) return input.businessName.trim();
  const composed = [input.firstName, input.lastName].map((p) => (p ?? "").trim()).filter(Boolean).join(" ");
  if (composed) return composed;
  if (input.primaryEmail?.trim()) return input.primaryEmail.trim();
  if (input.primaryPhone?.trim()) {
    return formatPhoneDisplay(input.primaryPhone.trim());
  }
  if (input.socialHandle?.trim()) return input.socialHandle.trim();
  if (input.fallbackLabel?.trim()) return input.fallbackLabel.trim();
  return "Anonymous";
}
function formatPhoneDisplay(e164) {
  const nanp = e164.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (nanp) {
    return `+1 (${nanp[1]}) ${nanp[2]}-${nanp[3]}`;
  }
  return e164;
}

// src/lib/user-display.ts
function userDisplayLabel(user) {
  if (!user) return "Unknown user";
  const composed = [user.firstName, user.lastName].map((part) => (part ?? "").trim()).filter(Boolean).join(" ");
  return composed || user.displayName?.trim() || user.email?.trim() || user.phone?.trim() || "Unknown user";
}

// src/types/api.ts
var API_PREFIX = "/api/v1";

// src/types/realtime.ts
var CLIENT_VISIBLE_TOOL_NAMES = [
  "search_vehicles",
  "search_facets",
  "get_vehicle",
  "rooftop_context",
  "create_cards",
  "brave_search",
  "submit_contact",
  "submit_vehicle",
  "submit_tradein",
  "submit_appointment"
];
var CLIENT_TOOL_EVENT_META = {
  search_vehicles: { type: "search_vehicles", label: "Searching vehicles" },
  search_facets: { type: "search_facets", label: "Checking inventory" },
  get_vehicle: { type: "get_vehicle", label: "Getting vehicle details" },
  rooftop_context: {
    type: "rooftop_context",
    label: "Getting dealer information"
  },
  create_cards: { type: "create_cards", label: "Showing cards" },
  brave_search: { type: "brave_search", label: "Searching the web" },
  submit_contact: { type: "submit_contact", label: "Saving information" },
  submit_vehicle: { type: "submit_vehicle", label: "Saving interest" },
  submit_tradein: { type: "submit_tradein", label: "Valuating trade-in" },
  submit_appointment: {
    type: "submit_appointment",
    label: "Scheduling appointment"
  }
};
var INTERNAL_TOOL_EVENT_META = {
  type: "internal_tool",
  label: "Thinking"
};
var CLIENT_TOOL_EVENT_TYPES = [
  "search_vehicles",
  "search_facets",
  "get_vehicle",
  "rooftop_context",
  "create_cards",
  "brave_search",
  "submit_contact",
  "submit_vehicle",
  "submit_tradein",
  "submit_appointment",
  "internal_tool"
];
export {
  A2pBrandTypeSchema,
  A2pBusinessProfileSchema,
  A2pCampaignStatusSchema,
  A2pCampaignUseCaseSchema,
  A2pJobPositionSchema,
  A2pStatusSchema,
  A2pSubmitBrandSchema,
  A2pSubmitCampaignSchema,
  A2pVerifyOtpSchema,
  AAP_EXTENSION_URI,
  AAP_METHODS,
  AAP_SKILL_IDS,
  AI_USAGE_KINDS,
  API_PREFIX,
  AapValidationResultSchema,
  AccessibleDealershipSchema,
  ActivityActorTypeSchema,
  ActivityDirectionSchema,
  ActivityListSchema,
  ActivityPageSchema,
  ActivitySchema,
  AddTagActionConfigSchema,
  AdminActivityListSchema,
  AdminActivitySchema,
  AdminMeSchema,
  AdminSchema,
  AgentCardExtensionSchema,
  AgentCardInterfaceSchema,
  AgentCardSchema,
  AgentCardSkillSchema,
  AiAgentChannelAssignmentListSchema,
  AiAgentChannelAssignmentSchema,
  AiAgentDetailSchema,
  AiAgentDispatchCheckRequestSchema,
  AiAgentDispatchDecisionSchema,
  AiAgentDispatchSkipReasonSchema,
  AiAgentListSchema,
  AiAgentRespondToSchema,
  AiAgentResponseSplitModeSchema,
  AiAgentSchema,
  AiAgentSkillSchema,
  AiAgentStatusSchema,
  AiAssignmentMediumSchema,
  AiRooftopPromptListSchema,
  AiRooftopPromptSchema,
  AiSkillListSchema,
  AiSkillSchema,
  AiUsageBreakdownRowSchema,
  AiUsageSummarySchema,
  AppMeSchema,
  AppointmentListSchema,
  AppointmentPageSchema,
  AppointmentSchema,
  AppointmentStatusSchema,
  AppointmentTransportTypeSchema,
  AppointmentTypeSchema,
  AssignActionConfigSchema,
  AssignTaskSchema,
  AttachmentUploadUrlResultSchema,
  AutomationActionTypeSchema,
  AutomationConditionsSchema,
  AutomationRuleSchema,
  AvatarUploadUrlResultSchema,
  BillingRangeSchema,
  BrandColorPairSchema,
  BrandImageSchema,
  CA_POSTAL_RE,
  CA_PROVINCES,
  CA_TIMEZONES,
  CHANGE_FIELD_LABELS,
  CLIENT_TOOL_EVENT_META,
  CLIENT_TOOL_EVENT_TYPES,
  CLIENT_VISIBLE_TOOL_NAMES,
  CONTACT_LANGUAGES,
  CONTACT_STATUS_LABELS,
  COUNTRIES,
  CaPostalCodeSchema,
  CannedResponseListSchema,
  CannedResponseSchema,
  ChannelCapabilitySchema,
  ChannelConflictResponseSchema,
  ChannelListSchema,
  ChannelOnboardingInfoSchema,
  ChannelPageSchema,
  ChannelSchema,
  ChannelStatusSchema,
  ChannelTypeSchema,
  CheckEmailAddressSchema,
  ConnectAapChannelSchema,
  ConnectApiChannelSchema,
  ConnectEmailChannelSchema,
  ConnectTelegramChannelSchema,
  ConnectWhatsAppEmbeddedSignupSchema,
  ConnectWidgetChannelSchema,
  ConsentChannelTypeSchema,
  ConsentEventListSchema,
  ConsentEventPageSchema,
  ConsentEventSchema,
  ConsentEventTypeSchema,
  ConsentScopeSchema,
  ConsentSourceSchema,
  ContactChannelListSchema,
  ContactChannelPageSchema,
  ContactChannelSchema,
  ContactChannelTypeSchema,
  ContactIntegrationSyncSchema,
  ContactLanguageSchema,
  ContactListSchema,
  ContactPageSchema,
  ContactSchema,
  ContactStatusSchema,
  ContactTagListSchema,
  ContactTagSchema,
  ContactVehicleListSchema,
  ContactVehiclePageSchema,
  ContactVehicleRelationshipSchema,
  ContactVehicleSchema,
  CountryCodeSchema,
  CreateActivitySchema,
  CreateAiAgentChannelAssignmentSchema,
  CreateAiAgentSchema,
  CreateAiAgentSkillSchema,
  CreateAiSkillSchema,
  CreateAppointmentSchema,
  CreateAttachmentUploadUrlSchema,
  CreateAutomationRuleSchema,
  CreateAvatarUploadUrlSchema,
  CreateCannedResponseSchema,
  CreateChannelSchema,
  CreateConsentEventSchema,
  CreateContactChannelBodySchema,
  CreateContactChannelSchema,
  CreateContactSchema,
  CreateContactTagSchema,
  CreateContactVehicleSchema,
  CreateDealershipSchema,
  CreateFollowUpSchema,
  CreateIntegrationSchema,
  CreateInventoryImportSchema,
  CreateInventoryVehicleSchema,
  CreateLeadSchema,
  CreateOutreachNoteSchema,
  CreateOutreachProspectSchema,
  CreateRooftopSchema,
  CreateServiceTypeSchema,
  CreateSftpAccountSchema,
  CreateTagSchema,
  CreateTaskActionConfigSchema,
  CreateTaskSchema,
  CreateTelephonyAccountSchema,
  CreateTradeInSchema,
  CreateUserMembershipSchema,
  CreateUserSchema,
  CreateVehicleInterestSchema,
  DEFAULT_REMINDER_TEMPLATE,
  DayHoursSchema,
  DealerInformationRequestSchema,
  DealerInformationResponseSchema,
  DealershipBillingSummarySchema,
  DealershipChoiceListSchema,
  DealershipChoiceSchema,
  DealershipCountrySchema,
  DealershipDetailSchema,
  DealershipListSchema,
  DealershipSchema,
  DealershipStatusSchema,
  DeliveryStatusSchema,
  E164_RE,
  EIN_RE,
  EinSchema,
  EmailAddressAvailabilitySchema,
  EmailCodeLoginResultSchema,
  EmailLocalPartSchema,
  EmailSchema,
  EmailSubdomainSchema,
  FollowUpKindSchema,
  FollowUpListSchema,
  FollowUpPageSchema,
  FollowUpSchema,
  FollowUpStatusSchema,
  GoogleCallbackSchema,
  HealthStatusSchema,
  HexColorSchema,
  INTERNAL_TOOL_EVENT_META,
  INVENTORY_PROVIDERS,
  INVENTORY_PROVIDER_IDS,
  INVENTORY_STATUS_LABELS,
  ImportOutreachProspectsSchema,
  ImportOutreachResultSchema,
  InboxVisibilitySchema,
  IntegrationBackfillStateSchema,
  IntegrationListSchema,
  IntegrationLocationsMappingSchema,
  IntegrationProviderLabels,
  IntegrationProviderSchema,
  IntegrationSchema,
  IntegrationStatusSchema,
  InternalNoteSchema,
  InventoryFacetsSchema,
  InventoryImportListSchema,
  InventoryImportProviderSchema,
  InventoryImportSchema,
  InventoryImportTypeSchema,
  InventoryQuerySchema,
  InventoryRangeSchema,
  InventoryRuleCombinatorSchema,
  InventoryRuleFieldSchema,
  InventoryRuleGroupSchema,
  InventoryRuleOperatorSchema,
  InventoryRuleSchema,
  InventorySearchFiltersSchema,
  InventorySearchRequestSchema,
  InventorySearchResponseSchema,
  InventorySortFieldSchema,
  InventoryStatusSchema,
  InventorySuggestRequestSchema,
  InventorySuggestResponseSchema,
  InventorySuggestionSchema,
  InventoryVehicleListSchema,
  InventoryVehiclePageSchema,
  InventoryVehicleRequestSchema,
  InventoryVehicleSchema,
  InviteLocationsSchema,
  InviteUserSchema,
  JsonRpcErrorSchema,
  JsonRpcRequestSchema,
  LEAD_STATUS_LABELS,
  LeadAiSummaryResultSchema,
  LeadAssignSchema,
  LeadBoardColumnSchema,
  LeadBoardSchema,
  LeadFeedSchema,
  LeadListSchema,
  LeadPageSchema,
  LeadPrimaryChannelSchema,
  LeadSchema,
  LeadSnoozeSchema,
  LeadStatusSchema,
  LeadSubmitRequestSchema,
  LeadSubmitResponseSchema,
  ListNotificationsQuerySchema,
  ListTasksQuerySchema,
  MEMBERSHIP_ROLE_LABELS,
  MENTION_REGEX,
  MembershipRoleSchema,
  MergeContactsResultSchema,
  MergeContactsSchema,
  MoveTaskSchema,
  NotificationCategorySchema,
  NotificationCountSchema,
  NotificationEventPreferenceSchema,
  NotificationEventTypeSchema,
  NotificationPageSchema,
  NotificationSchema,
  NotificationSettingsSchema,
  NotificationSeveritySchema,
  OPERATOR_PAUSABLE_CHANNEL_TYPES,
  OUTREACH_STAGE_LABELS,
  OUTREACH_TERMINAL_STAGES,
  OnboardingCreateSchema,
  OptionalCaPostalCodeSchema,
  OptionalCountryCodeSchema,
  OptionalEinSchema,
  OptionalEmailSchema,
  OptionalPhoneE164Schema,
  OptionalStateOrProvinceCodeSchema,
  OptionalUrlSchema,
  OptionalUsStateCodeSchema,
  OptionalUsZipSchema,
  OptionalVinSchema,
  OptionalZipOrPostalCodeSchema,
  OutboundAttachmentKindSchema,
  OutboundAttachmentSchema,
  OutreachListQuerySchema,
  OutreachNoteKindSchema,
  OutreachNoteSchema,
  OutreachProspectPageSchema,
  OutreachProspectSchema,
  OutreachStageSchema,
  OutreachSummarySchema,
  PROVIDER_MANAGED_CHANNEL_TYPES,
  PhoneE164Schema,
  PlatformBillingSummarySchema,
  PreferredCommunicationChannelSchema,
  RangeFacetSchema,
  ReminderStepSchema,
  ReminderTemplateSchema,
  ReportVolumeSchema,
  RequestEmailCodeSchema,
  ResolveOrCreateContactSchema,
  RooftopFacetSchema,
  RooftopListSchema,
  RooftopSchema,
  RooftopStatusSchema,
  RotateSftpAccountSchema,
  SCRAPING_PROVIDER_IDS,
  SelectDealershipSchema,
  SendLeadMessageSchema,
  ServiceTypeCategorySchema,
  ServiceTypeSchema,
  SetAvatarSchema,
  SftpAccountRecordSchema,
  SftpAccountStatusSchema,
  SftpAccountWithSecretSchema,
  SftpCreateFolderRequestSchema,
  SftpDeletePathRequestSchema,
  SftpDownloadUrlResponseSchema,
  SftpFileEntrySchema,
  SftpFolderSchema,
  SftpListResponseSchema,
  SftpPasswordSchema,
  SftpUploadUrlRequestSchema,
  SftpUploadUrlResponseSchema,
  SftpUsernameSchema,
  SnoozeTaskSchema,
  StartConversationSchema,
  StartMetaOAuthSchema,
  StateOrProvinceCodeSchema,
  SuggestedResponseSchema,
  TELEPHONY_PRICING,
  TagListSchema,
  TagSchema,
  TaskCountsSchema,
  TaskPageSchema,
  TaskReasonSchema,
  TaskSchema,
  TaskStatusSchema,
  TaskTypeSchema,
  TaskViewSchema,
  TelephonyAccountListSchema,
  TelephonyAccountSchema,
  TelephonyAccountSearchResultListSchema,
  TelephonyAccountSearchResultSchema,
  TelephonyAvailableNumberListSchema,
  TelephonyAvailableNumberSchema,
  TelephonyBrandStatusSchema,
  TelephonyBusinessTypeSchema,
  TelephonyE911AddressSchema,
  TelephonyEmergencyStatusSchema,
  TelephonyNumberTypeSchema,
  TelephonyPhoneNumberListSchema,
  TelephonyPhoneNumberSchema,
  TelephonyPurchaseNumberSchema,
  TelephonySearchNumbersSchema,
  TelephonyUpdateNumberSchema,
  TelephonyUsageRangeSchema,
  TelephonyUsageRowSchema,
  TelephonyUsageSummarySchema,
  TermFacetSchema,
  TradeInConditionSchema,
  TradeInListSchema,
  TradeInPageSchema,
  TradeInSchema,
  TradeInStatusSchema,
  TranscribeAttachmentResultSchema,
  TranscribeAttachmentSchema,
  USAGE_TYPES,
  US_STATES,
  US_TIMEZONES,
  US_ZIP_RE,
  UpdateActivitySchema,
  UpdateAiAgentSchema,
  UpdateAiSkillSchema,
  UpdateAppointmentSchema,
  UpdateAutomationRuleSchema,
  UpdateChannelSchema,
  UpdateContactChannelSchema,
  UpdateContactSchema,
  UpdateDealershipSchema,
  UpdateIntegrationSchema,
  UpdateInventoryImportSchema,
  UpdateInventoryVehicleSchema,
  UpdateLeadSchema,
  UpdateNotificationSettingsSchema,
  UpdateOutreachProspectSchema,
  UpdateRooftopScheduleSchema,
  UpdateRooftopSchema,
  UpdateServiceTypeSchema,
  UpdateSftpAccountSchema,
  UpdateTagSchema,
  UpdateTaskSchema,
  UpdateTelephonyAccountSchema,
  UpdateUserMembershipSchema,
  UpdateUserSchema,
  UpsertAiRooftopPromptSchema,
  UrlSchema,
  UsStateCodeSchema,
  UsZipSchema,
  UserKindSchema,
  UserListSchema,
  UserMembershipListSchema,
  UserMembershipSchema,
  UserSchema,
  UserStatusSchema,
  VIN_RE,
  VehicleConditionSchema,
  VehicleInterestLevelSchema,
  VehicleInterestListSchema,
  VehicleInterestPageSchema,
  VehicleInterestSchema,
  VehicleInterestStatusSchema,
  VehicleSourceSchema,
  VehicleStatusSchema,
  VerifyEmailCodeSchema,
  VerifyIntegrationLocationSchema,
  VerifyIntegrationTokenRequestSchema,
  VerifyIntegrationTokenResponseSchema,
  VinSchema,
  WEEKDAYS,
  WeeklyBusinessHoursSchema,
  WidgetBrandSchema,
  WidgetCustomizationSchema,
  ZipOrPostalCodeSchema,
  caProvinceLabel,
  changeFieldLabel,
  channelStatusActions,
  computeLeadScore,
  contactDisplayName,
  contactLanguageLabel,
  contactLanguageVoiceLocale,
  contactStatusLabel,
  countryLabel,
  currencyForCountry,
  extractMentionIds,
  formatChangeValue,
  formatInTimeZone,
  formatMention,
  formatPhoneDisplay,
  formatUsd,
  inventoryProviderName,
  isAiUsageKind,
  isInventoryProviderId,
  isOperatorPausableChannelType,
  isProviderManagedChannelType,
  isScrapingProviderId,
  leadStatusLabel,
  normalizeContactLanguage,
  normalizeScrapeDomain,
  parseActivityChanges,
  renderEmailCodeEmail,
  renderInvitationEmail,
  replaceMentions,
  slugFromLabel,
  splitMentions,
  stateOptionsForCountry,
  summarizeActivityChanges,
  timezonesForCountry,
  translateCarrierError,
  truncateMentions,
  usStateLabel,
  userDisplayLabel,
  utcToWallTime,
  wallClockDate,
  wallTimeToUtc
};
//# sourceMappingURL=index.js.map